<template>
  <div class="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 min-h-screen">
    <!-- 顶部导航栏 -->
    <div
      class="bg-blue-500 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10"
    >
      <div class="flex items-center">
        <button
          class="bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-lg mr-4 transition-colors"
          @click="goBack"
        >
          <i class="fas fa-arrow-left"></i>
        </button>
        <h1 class="text-2xl font-semibold">订单查询</h1>
      </div>
      <div class="flex items-center">
        <span class="text-sm bg-blue-600 px-3 py-1 rounded-full">服务员系统</span>
      </div>
    </div>

    <!-- 筛选控制区域 -->
    <div class="p-4 bg-white shadow-sm">
      <div class="w-full">
        <div class="flex flex-wrap gap-3 items-end pb-1">
          <div class="flex flex-col shrink-0">
            <label class="text-sm font-medium text-gray-700 mb-1">开始日期</label>
            <input
              v-model="filters.startDate"
              type="date"
              class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div class="flex flex-col shrink-0">
            <label class="text-sm font-medium text-gray-700 mb-1">结束日期</label>
            <input
              v-model="filters.endDate"
              type="date"
              class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div class="flex flex-col shrink-0">
            <label class="text-sm font-medium text-gray-700 mb-1">餐厅筛选</label>
            <select
              v-model="filters.restaurant"
              disabled
              class="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[120px]"
              @change="onRestaurantChange"
            >
              <option value="" disabled>请选择餐厅</option>
              <option v-for="shop in shopList" :key="shop.shopId" :value="String(shop.shopId)">
                {{ shop.shopName }}
              </option>
            </select>
          </div>
          <div class="flex flex-col shrink-0">
            <label class="text-sm font-medium text-gray-700 mb-1">桌号筛选</label>
            <select
              v-model="filters.table"
              :disabled="!filters.restaurant"
              class="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[100px]"
              :class="{ 'bg-gray-100': !filters.restaurant }"
            >
              <option value="">全部桌号</option>
              <option v-for="table in availableTables" :key="table.tableId" :value="table.tableId">
                {{ table.tableName }}
              </option>
            </select>
          </div>

          <div class="flex flex-col shrink-0">
            <label class="text-sm font-medium text-gray-700 mb-1">订单号</label>
            <input
              v-model="filters.orderNo"
              type="text"
              placeholder="输入订单号"
              class="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[160px]"
            />
          </div>
          <div class="flex flex-col shrink-0">
            <label class="text-sm font-medium text-gray-700 mb-1">订单状态</label>
            <select
              v-model="filters.status"
              class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">全部状态</option>
              <option value="1">已点餐</option>
              <option value="2">待出餐</option>
              <option value="8">已出餐</option>
              <option value="10">已完成</option>
              <option value="999">已取消</option>
            </select>
          </div>
          <div class="flex flex-col shrink-0">
            <label class="text-sm font-medium text-gray-700 mb-1">支付状态</label>
            <select
              v-model="filters.paymentStatus"
              class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">全部支付状态</option>
              <option value="0">待支付</option>
              <option value="5">部分支付</option>
              <option value="10">已支付</option>
            </select>
          </div>
          <div class="flex flex-col shrink-0">
            <label class="text-sm font-medium text-gray-700 mb-1">退款状态</label>
            <select
              v-model="filters.refundStatus"
              class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">全部退款状态</option>
              <option value="0">待退款</option>
              <option value="10">已退款</option>
            </select>
          </div>
          <div class="flex flex-col shrink-0">
            <label class="text-sm font-medium text-gray-700 mb-1">打印状态</label>
            <div class="flex gap-1">
              <select
                v-model="filters.printOrder"
                aria-label="收银小票打印状态"
                class="border border-gray-300 rounded-lg px-2 py-2 text-sm"
                style="width: 108px"
              >
                <option value="">收银小票</option>
                <option value="0">未打印</option>
                <option value="1">已打印</option>
              </select>
              <select
                v-model="filters.printOrderSlip"
                aria-label="订单清单打印状态"
                class="border border-gray-300 rounded-lg px-2 py-2 text-sm"
                style="width: 108px"
              >
                <option value="">订单清单</option>
                <option value="0">未打印</option>
                <option value="1">已打印</option>
              </select>
            </div>
          </div>
          <div class="flex gap-3 ml-1 pb-[1px]">
            <button
              class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
              @click="applyFilters"
            >
              <i class="fas fa-search mr-2"></i>筛选
            </button>
            <button
              class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
              @click="clearFilters"
            >
              <i class="fas fa-times mr-2"></i>清除
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading && orderList.length === 0" class="text-center py-12">
      <i class="fas fa-spinner fa-spin text-4xl text-blue-500 mb-4"></i>
      <p class="text-gray-600">正在加载订单数据...</p>
    </div>

    <!-- 订单列表 -->
    <div v-else class="p-4">
      <div class="max-w-7xl mx-auto">
        <div v-if="orderList.length > 0" class="bg-white rounded-lg shadow-sm overflow-hidden">
          <div class="bg-gray-50 px-6 py-3 border-b" style="text-align: left">
            <h2 class="text-lg font-semibold text-gray-800"
              >订单查询列表 - 共 {{ totalRecords }} 条记录，当前页菜品总份数
              {{ totalFoodCount }}</h2
            >
          </div>
          <div class="divide-y divide-gray-200">
            <div
              v-for="order in orderList"
              :key="order.orderId"
              class="p-6 hover:bg-gray-50 transition-colors"
            >
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <div class="flex flex-wrap items-center gap-4 mb-3">
                    <div class="text-lg font-semibold text-gray-900">订单 #{{ order.orderNo }}</div>
                    <span
                      v-if="Number(order.orderType) === 1"
                      class="px-2 py-1 rounded-full text-xs font-medium"
                      style="background-color: #ffedd5; color: #c2410c"
                    >
                      POS订单
                    </span>
                    <div class="text-sm text-blue-600 font-medium">【{{ order.shopName }}】</div>
                    <div class="text-sm text-gray-600">
                      {{ order.tableName
                      }}<template v-if="order.seatNumber">/{{ order.seatNumber }}</template>
                    </div>
                    <span
                      v-if="getDineTypeText(order.dineType)"
                      class="px-2 py-1 rounded-full text-xs font-medium"
                      :class="getDineTypeColor(order.dineType)"
                    >
                      {{ getDineTypeText(order.dineType) }}
                    </span>
                    <div class="text-sm text-gray-600">{{ order.personCount }}人用餐</div>
                    <span
                      class="px-2 py-1 rounded-full text-xs font-medium"
                      :class="getStatusColor(order.orderStatus)"
                    >
                      {{ getStatusText(order.orderStatus) }}
                    </span>
                    <span
                      v-if="order.paymentStatusName"
                      class="px-2 py-1 rounded-full text-xs font-medium"
                      :class="getPaymentStatusColor(order.paymentStatusName)"
                    >
                      {{ order.paymentStatusName }}
                    </span>
                    <!-- 新增：列表上显示“有退款”标识 -->
                    <span
                      v-if="order.isRefund === 1"
                      class="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700"
                    >
                      有退款
                    </span>
                  </div>
                  <div class="flex items-center gap-6 text-sm text-gray-600 mb-3">
                    <div class="flex items-center">
                      <i class="fas fa-clock mr-1"></i>
                      {{ formatDateTime(order.createTime) }}
                    </div>
                    <div class="flex items-center">
                      <i class="fas fa-user mr-1"></i>
                      {{ order.userName || '服务员' }}
                    </div>
                    <div class="flex items-center">
                      <i class="fas fa-utensils mr-1"></i>
                      {{ order.foodList?.length || 0 }}道菜
                    </div>
                    <!-- 新增：自提时间显示 -->
                    <div
                      v-if="isSelfPickupOrder(order)"
                      class="flex items-center text-orange-600 font-medium"
                    >
                      <i class="fas fa-shopping-bag mr-1"></i>
                      自提：{{ formatSelfPickupTime(order) || '待确认' }}
                    </div>
                    <!-- 新增：取餐码显示 -->
                    <div
                      v-if="getDisplayPickCode(order)"
                      class="flex items-center text-green-600 font-medium"
                    >
                      <i class="fas fa-qrcode mr-1"></i>
                      取餐码：{{ getDisplayPickCode(order) }}
                    </div>
                    <div
                      v-if="isDeliveryOrder(order)"
                      class="flex items-center text-cyan-700 font-medium"
                    >
                      <i class="fas fa-map-marker-alt mr-1"></i>
                      送货地址：{{ order.consigneeAddress || '待填写地址' }}
                    </div>
                  </div>
                  <div class="text-sm text-gray-600" style="text-align: left">
                    {{
                      order.foodList?.slice(0, 3).map(formatFoodSummary).join('、') ||
                      '暂无菜品信息'
                    }}
                    <span v-if="order.foodList && order.foodList.length > 3">...</span>
                  </div>
                  <div
                    v-if="order.description"
                    class="mt-2 text-sm text-red-600 font-medium"
                    style="text-align: left"
                  >
                    <i class="fas fa-sticky-note mr-1"></i>备注：{{ order.description }}
                  </div>
                </div>
                <div class="flex flex-col items-end gap-3">
                  <div class="text-xl font-bold text-gray-900"
                    >¥{{ (order.totalAmount || 0).toFixed(2) }}
                  </div>
                  <div class="flex flex-wrap justify-end gap-2">
                    <button
                      class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                      @click="viewOrderDetail(order)"
                    >
                      <i class="fas fa-eye mr-1"></i>详情
                    </button>
                    <button
                      v-if="isUnpaidOrder(order)"
                      class="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                      @click="openHistoryPayment(order)"
                    >
                      <i class="fas fa-credit-card mr-1"></i>支付
                    </button>
                    <button
                      v-if="canGenerateInvoice(order)"
                      class="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm transition-colors"
                      @click="openInvoiceModal(order)"
                    >
                      <i class="fas fa-file-invoice mr-1"></i>开票
                    </button>
                    <button
                      v-if="canCancelOrder(order)"
                      class="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-colors"
                      @click="cancelOrderAction(order)"
                    >
                      <i class="fas fa-times mr-1"></i>取消订单
                    </button>
                    <div
                      v-if="
                        canPrintReceipt(order) ||
                        ((order.dineType === 5 || isDeliveryOrder(order)) &&
                          order.orderStatus !== 999)
                      "
                      class="flex overflow-hidden rounded text-sm text-white"
                    >
                      <button
                        v-if="canPrintReceipt(order)"
                        class="bg-purple-500 hover:bg-purple-600 px-2 py-1 transition-colors"
                        title="打印收银小票"
                        @click="printReceipt(order)"
                      >
                        <i class="fas fa-receipt mr-1"></i>收银 ({{ order.printOrder ?? '-' }})
                      </button>
                      <button
                        v-if="
                          canPrintReceipt(order) && order.dineType !== 5 && !isDeliveryOrder(order)
                        "
                        class="bg-purple-500 hover:bg-purple-600 border-l border-purple-400 px-2 py-1 transition-colors"
                        title="打印订单小票"
                        @click="printOrderSlip(order)"
                      >
                        订单 ({{ order.printOrderSlip ?? '-' }})
                      </button>
                      <button
                        v-if="order.dineType === 5"
                        class="bg-purple-500 hover:bg-purple-600 border-l border-purple-400 px-2 py-1 transition-colors"
                        title="打印预定订单小票"
                        @click="printBookOrderSlip(order)"
                      >
                        预定 ({{ order.printBookOrder ?? '-' }})
                      </button>
                      <button
                        v-if="isDeliveryOrder(order)"
                        class="bg-purple-500 hover:bg-purple-600 border-l border-purple-400 px-2 py-1 transition-colors"
                        title="打印外卖订单小票"
                        @click="printDeliveryOrderSlip(order)"
                      >
                        外卖 ({{ order.printDeliveryOrder ?? '-' }})
                      </button>
                    </div>
                    <button
                      v-if="canPrintPickLabel(order)"
                      class="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1 rounded text-sm transition-colors"
                      @click="printPickLabel(order)"
                    >
                      <i class="fas fa-tag mr-1"></i>出餐标签 ({{ order.printOrderFood ?? '-' }})
                    </button>
                    <button
                      v-if="canApplyRefund(order)"
                      class="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                      @click="openRefundModal(order)"
                    >
                      <i class="fas fa-undo mr-1"></i>申请退款
                    </button>
                    <button
                      v-if="canCompleteRefund(order)"
                      :disabled="
                        order.orderId !== undefined &&
                        completingRefundOrderIds.includes(order.orderId)
                      "
                      class="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      @click="completeRefundAction(order)"
                    >
                      <i class="fas fa-check-circle mr-1"></i>完成退款
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="totalPages > 1"
            class="border-t bg-gray-50 px-4 py-4 flex flex-wrap items-center justify-between gap-3"
          >
            <div class="text-sm text-gray-600">
              第 {{ currentPage }} / {{ totalPages }} 页，每页 {{ pageSize }} 条
            </div>
            <div class="flex flex-wrap items-center justify-end gap-2">
              <button
                :disabled="currentPage === 1"
                class="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                @click="changePage(currentPage - 1)"
              >
                上一页
              </button>
              <button
                v-for="page in visiblePages"
                :key="page"
                class="min-w-[36px] border rounded px-3 py-1.5 text-sm transition-colors"
                :class="
                  page === currentPage
                    ? 'border-blue-500 bg-blue-500 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                "
                @click="changePage(page)"
              >
                {{ page }}
              </button>
              <button
                :disabled="currentPage === totalPages"
                class="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                @click="changePage(currentPage + 1)"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="!loading && orderList.length === 0" class="text-center py-12">
      <i class="fas fa-receipt text-6xl text-gray-300 mb-4"></i>
      <h3 class="text-xl font-semibold text-gray-500 mb-2">暂无订单</h3>
      <p class="text-gray-400">没有符合当前查询条件的订单记录</p>
    </div>

    <!-- 订单详情模态框 -->
    <div
      v-if="showOrderDetail"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      @click="closeOrderDetail"
    >
      <div
        class="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto"
        @click.stop
      >
        <div class="bg-blue-500 text-white px-6 py-4 flex items-center justify-between">
          <h3 class="text-xl font-semibold">订单详情</h3>
          <button class="text-white hover:text-gray-200" @click="closeOrderDetail">
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>
        <div v-if="currentOrderDetail" class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div class="space-y-4">
              <h4 class="text-lg font-semibold text-gray-800 border-b pb-2">基本信息</h4>
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-gray-600">订单编号：</span>
                  <span class="font-medium">{{ currentOrderDetail.orderNo }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">用户Id：</span>
                  <span class="font-medium">{{ currentOrderDetail.customerId || '' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">餐厅：</span>
                  <span class="font-medium text-blue-600">{{ currentOrderDetail.shopName }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">桌号：</span>
                  <span class="font-medium">{{ currentOrderDetail.tableName }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">用餐人数：</span>
                  <span class="font-medium">{{ currentOrderDetail.personCount }}人</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">服务员：</span>
                  <span class="font-medium">{{ currentOrderDetail.userName || '服务员' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">订单状态：</span>
                  <span
                    class="font-medium"
                    :class="getStatusTextColor(currentOrderDetail.orderStatus)"
                    >{{ getStatusText(currentOrderDetail.orderStatus) }}</span
                  >
                </div>
                <div v-if="currentOrderDetail.consigneeName" class="flex justify-between gap-4">
                  <span class="text-gray-600 shrink-0">联系人：</span>
                  <span class="font-medium text-cyan-700 text-right break-all">
                    {{ currentOrderDetail.consigneeName }}
                  </span>
                </div>
                <div v-if="currentOrderDetail.consigneePhone" class="flex justify-between gap-4">
                  <span class="text-gray-600 shrink-0">电话：</span>
                  <span class="font-medium text-cyan-700 text-right break-all">
                    {{ currentOrderDetail.consigneePhone }}
                  </span>
                </div>
                <template v-if="isDeliveryOrder(currentOrderDetail)">
                  <div class="flex justify-between gap-4">
                    <span class="text-gray-600 shrink-0">地址：</span>
                    <span class="font-medium text-cyan-700 text-right">
                      {{ currentOrderDetail.consigneeAddress || '-' }}
                    </span>
                  </div>
                </template>
                <!-- 新增：取餐码显示 -->
                <div v-if="getDisplayPickCode(currentOrderDetail)" class="flex justify-between">
                  <span class="text-gray-600">取餐码：</span>
                  <span class="font-medium text-green-600">
                    {{ getDisplayPickCode(currentOrderDetail) }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">下单时间：</span>
                  <span class="font-medium">{{
                    formatDateTime(currentOrderDetail.createTime)
                  }}</span>
                </div>
                <div v-if="appointmentTimeText" class="flex justify-between gap-4">
                  <span class="text-gray-600 shrink-0">预约时间：</span>
                  <span class="font-medium text-cyan-700 text-right">
                    {{ appointmentTimeText }}
                  </span>
                </div>
                <div v-if="currentOrderDetail.description" class="flex justify-between">
                  <span class="text-gray-600">备注：</span>
                  <span class="font-medium text-red-600">{{ currentOrderDetail.description }}</span>
                </div>
                <!-- 新增：退款信息显示（isRefund = 1 时显示） -->
                <template v-if="(currentOrderDetail as any).isRefund === 1">
                  <div class="flex justify-between">
                    <span class="text-gray-600">是否有退款：</span>
                    <span class="font-medium text-green-600">是</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">退款金额：</span>
                    <span class="font-medium text-orange-600">
                      ¥{{ ((currentOrderDetail as any).refundAmount || 0).toFixed(2) }}
                    </span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">退款状态：</span>
                    <span
                      class="font-medium"
                      :class="getRefundStatusTextColor((currentOrderDetail as any).refundStatus)"
                    >
                      {{ getRefundStatusText((currentOrderDetail as any).refundStatus) }}
                    </span>
                  </div>
                </template>
              </div>
            </div>
            <div class="space-y-4">
              <h4 class="text-lg font-semibold text-gray-800 border-b pb-2">金额统计</h4>
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-gray-600">菜品总计：</span>
                  <span class="font-medium"
                    >¥{{ (currentOrderDetail.foodAmount || 0).toFixed(2) }}</span
                  >
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">服务费：</span>
                  <span class="font-medium"
                    >¥{{ (currentOrderDetail.serviceAmount || 0).toFixed(2) }}</span
                  >
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">打包费：</span>
                  <span class="font-medium"
                    >¥{{ (currentOrderDetail.packageAmount || 0).toFixed(2) }}</span
                  >
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">折扣金额：</span>
                  <span class="font-medium text-red-600"
                    >-¥{{ (currentOrderDetail.discountAmount || 0).toFixed(2) }}</span
                  >
                </div>
                <div class="flex justify-between text-lg font-bold border-t pt-2">
                  <span>订单总计：</span>
                  <span class="text-red-600"
                    >¥{{ (currentOrderDetail.totalAmount || 0).toFixed(2) }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- 订单详情 Tab 切换 -->
          <div class="space-y-4">
            <div class="border-b mb-2 flex flex-wrap gap-4">
              <button
                class="pb-2 text-sm font-medium border-b-2"
                :class="
                  detailTab === 'food'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500'
                "
                @click="detailTab = 'food'"
              >
                菜品明细
              </button>
              <button
                v-if="currentOrderCoupons.length > 0"
                class="pb-2 text-sm font-medium border-b-2"
                :class="
                  detailTab === 'coupon'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500'
                "
                @click="detailTab = 'coupon'"
              >
                卡券列表
              </button>
              <button
                v-if="(currentOrderDetail.paymentList || []).length > 0"
                class="pb-2 text-sm font-medium border-b-2"
                :class="
                  detailTab === 'payment'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500'
                "
                @click="detailTab = 'payment'"
              >
                支付明细
              </button>
              <button
                v-if="(currentOrderDetail as any).isRefund === 1"
                class="pb-2 text-sm font-medium border-b-2"
                :class="
                  detailTab === 'refund'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500'
                "
                @click="detailTab = 'refund'"
              >
                退款明细
              </button>
              <button
                class="pb-2 text-sm font-medium border-b-2"
                :class="
                  detailTab === 'invoice'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500'
                "
                @click="detailTab = 'invoice'"
              >
                发票查看
              </button>
            </div>

            <!-- 菜品明细 Tab -->
            <div v-if="detailTab === 'food'" class="space-y-2">
              <div class="overflow-x-auto">
                <table class="w-full min-w-[800px] table-fixed text-sm">
                  <colgroup>
                    <col />
                    <col class="w-32" />
                    <col class="w-24" />
                    <col class="w-16" />
                    <col class="w-[72px]" />
                    <col class="w-24" />
                    <col class="w-24" />
                  </colgroup>
                  <thead class="bg-gray-50 whitespace-nowrap">
                    <tr>
                      <th class="text-left p-3 font-medium text-gray-700">菜品名称</th>
                      <th class="text-center p-3 font-medium text-gray-700">单价</th>
                      <th class="text-center p-3 font-medium text-gray-700">折扣金额</th>
                      <th class="text-center p-3 font-medium text-gray-700">数量</th>
                      <th class="text-center p-3 font-medium text-gray-700">类型</th>
                      <th class="text-center p-3 font-medium text-gray-700">菜品状态</th>
                      <th class="text-right p-3 font-medium text-gray-700">小计</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    <tr v-for="dish in currentOrderDetail.foodList || []" :key="dish.orderFoodId">
                      <td class="p-3 font-medium text-left whitespace-normal break-words">
                        <div>{{ foodDisplayName(dish) }}</div>
                        <div v-if="dish.foodCookDesc?.trim()" class="mt-1 text-xs text-orange-700">
                          {{ dish.foodCookDesc }}
                        </div>
                      </td>
                      <td class="p-3 text-center whitespace-nowrap">
                        <span>¥{{ (Number(dish.price) || 0).toFixed(2) }}</span>
                        <span
                          v-if="
                            dish.sourcePrice != null &&
                            Number(dish.sourcePrice) !== Number(dish.price || 0)
                          "
                          class="block text-xs text-gray-400"
                        >
                          / ¥{{ (Number(dish.sourcePrice) || 0).toFixed(2) }}
                        </span>
                      </td>
                      <td class="p-3 text-center whitespace-nowrap">
                        ¥{{ (dish.discountAmount ?? 0).toFixed(2) }}
                      </td>
                      <td class="p-3 text-center whitespace-nowrap">{{ dish.quantity }}</td>
                      <td class="p-3 text-center whitespace-nowrap">
                        <span v-if="dish.addType === 1" class="text-blue-600">正常</span>
                        <span v-else-if="dish.addType === 2" class="text-orange-600">加菜</span>
                        <span v-else class="text-gray-600">未知</span>
                      </td>
                      <td class="p-3 text-center whitespace-nowrap">
                        <span v-if="dish.status === 1" class="text-green-600">已点餐</span>
                        <span v-else-if="dish.status === 2" class="text-green-600">待出餐</span>
                        <span v-else-if="dish.status === 8" class="text-green-600">已出餐</span>
                        <span v-else-if="dish.status === 9" class="text-green-600">已上菜</span>
                        <span v-else-if="dish.status === 999" class="text-gray-600">已取消</span>
                      </td>
                      <td class="p-3 text-right font-medium whitespace-nowrap">
                        ¥{{ ((dish.price || 0) * (dish.quantity || 0)).toFixed(2) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 卡券列表 Tab -->
            <div v-if="detailTab === 'coupon'" class="space-y-2">
              <div v-if="currentOrderCoupons.length > 0" class="overflow-x-auto pb-2">
                <div class="min-w-max">
                  <table class="text-sm table-auto">
                    <thead class="bg-gray-50">
                      <tr>
                        <th
                          class="text-left p-3 font-medium text-gray-700 min-w-[120px] whitespace-nowrap"
                        >
                          券名称
                        </th>
                        <th
                          class="text-left p-3 font-medium text-gray-700 min-w-[220px] whitespace-nowrap"
                        >
                          券码
                        </th>
                        <th
                          class="text-left p-3 font-medium text-gray-700 min-w-[180px] whitespace-nowrap"
                        >
                          来源菜品
                        </th>
                        <th class="text-center p-3 font-medium text-gray-700 min-w-[100px]">
                          来源类型
                        </th>
                        <th
                          class="text-center p-3 font-medium text-gray-700 min-w-[80px] whitespace-nowrap"
                        >
                          序号
                        </th>
                        <th
                          class="text-center p-3 font-medium text-gray-700 min-w-[120px] whitespace-nowrap"
                        >
                          状态
                        </th>
                        <th
                          class="text-center p-3 font-medium text-gray-700 min-w-[100px] whitespace-nowrap"
                        >
                          可分享
                        </th>
                        <th
                          class="text-center p-3 font-medium text-gray-700 min-w-[90px] whitespace-nowrap"
                        >
                          禁用
                        </th>
                        <th
                          class="text-center p-3 font-medium text-gray-700 min-w-[180px] whitespace-nowrap"
                        >
                          获取时间
                        </th>
                        <th class="text-center p-3 font-medium text-gray-700 min-w-[180px]">
                          使用/取消时间
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200">
                      <tr
                        v-for="coupon in currentOrderCoupons"
                        :key="coupon.customerCouponId || coupon.couponNumber"
                      >
                        <td class="p-3 font-medium text-left">
                          <div>{{ coupon.couponName || '-' }}</div>
                          <div v-if="coupon.description" class="text-xs text-gray-500 font-normal">
                            {{ coupon.description }}
                          </div>
                        </td>
                        <td class="p-3 text-left text-gray-600 break-all">
                          {{ coupon.couponNumber || '-' }}
                        </td>
                        <td class="p-3 text-left text-gray-600 whitespace-nowrap">
                          {{ coupon.sourceFoodName }}
                          <span class="text-gray-400"> / {{ coupon.sourceFoodSpecName }}</span>
                        </td>
                        <td class="p-3 text-center text-gray-600 whitespace-nowrap">
                          {{ getCouponSourceTypeText(coupon.sourceType) }}
                        </td>
                        <td class="p-3 text-center whitespace-nowrap">{{
                          coupon.couponSeq || '-'
                        }}</td>
                        <td class="p-3 text-center whitespace-nowrap">
                          <span class="font-medium" :class="getCouponStatusClass(coupon.status)">
                            {{ getCouponStatusText(coupon.status) }}
                          </span>
                        </td>
                        <td class="p-3 text-center text-gray-600 whitespace-nowrap">
                          {{ getCouponShareText(coupon.isShare) }}
                        </td>
                        <td class="p-3 text-center whitespace-nowrap">
                          <span :class="getCouponDisabledClass(coupon.disabled)">
                            {{ getCouponDisabledText(coupon.disabled) }}
                          </span>
                        </td>
                        <td class="p-3 text-center text-gray-600 whitespace-nowrap">
                          {{ formatDateTime(coupon.acquireTime) || '-' }}
                        </td>
                        <td class="p-3 text-center text-gray-600 whitespace-nowrap">
                          {{
                            formatDateTime(coupon.useTime) ||
                            formatDateTime(coupon.cancelTime) ||
                            '-'
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">暂无卡券</div>
            </div>

            <!-- 支付明细 Tab -->
            <div v-if="detailTab === 'payment'" class="space-y-2">
              <div
                v-if="(currentOrderDetail.paymentList || []).length > 0"
                class="overflow-x-auto bg-gray-50 rounded-lg"
              >
                <table class="w-full text-sm">
                  <thead class="bg-gray-100">
                    <tr>
                      <th class="text-left p-3 font-medium text-gray-700">支付单号</th>
                      <th class="text-left p-3 font-medium text-gray-700">支付方式</th>
                      <th class="text-center p-3 font-medium text-gray-700">支付状态</th>
                      <th class="text-right p-3 font-medium text-gray-700">金额</th>
                      <th class="text-center p-3 font-medium text-gray-700">支付时间</th>
                      <th class="text-center p-3 font-medium text-gray-700">支付线路</th>
                      <th class="text-center p-3 font-medium text-gray-700">操作</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    <tr
                      v-for="(pay, payIndex) in currentOrderDetail.paymentList || []"
                      :key="pay.orderPaymentId || pay.paymentNo || payIndex"
                    >
                      <td class="p-3 text-left font-medium">{{ pay.paymentNo || '-' }}</td>
                      <td class="p-3 text-left">{{ pay.paymentMethodName || '-' }}</td>
                      <td class="p-3 text-center">
                        {{ getPaymentRecordStatusText(pay.paymentStatus) }}
                      </td>
                      <td class="p-3 text-right text-red-600 font-medium">
                        ¥{{ (pay.amount || 0).toFixed(2) }}
                      </td>
                      <td class="p-3 text-center">{{ formatDateTime(pay.paymentTime) }}</td>
                      <td class="p-3 text-center">
                        {{ pay.paymentLine === 1 ? '线上' : pay.paymentLine === 0 ? '线下' : '-' }}
                      </td>
                      <td class="p-3 text-center whitespace-nowrap">
                        <button
                          v-if="canRefreshFailedScanPayment(pay)"
                          :disabled="refreshingPaymentRecordKey === getPaymentRecordKey(pay)"
                          class="px-3 py-1.5 rounded-md bg-green-500 text-white text-xs font-medium hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                          @click="refreshFailedScanPayment(pay)"
                        >
                          <template v-if="refreshingPaymentRecordKey === getPaymentRecordKey(pay)">
                            <i class="fas fa-spinner fa-spin mr-1"></i>查询中
                          </template>
                          <template v-else>重新查询支付结果</template>
                        </button>
                        <span v-else class="text-gray-400">-</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
                暂无支付记录
              </div>
            </div>

            <!-- 退款明细 Tab -->
            <div
              v-if="detailTab === 'refund' && (currentOrderDetail as any).isRefund === 1"
              class="space-y-2"
            >
              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="text-left p-3 font-medium text-gray-700">退款单号</th>
                      <th class="text-center p-3 font-medium text-gray-700">退款方式</th>
                      <th class="text-center p-3 font-medium text-gray-700">退款金额</th>
                      <th class="text-center p-3 font-medium text-gray-700">退款状态</th>
                      <th class="text-center p-3 font-medium text-gray-700">退款时间</th>
                      <th class="text-left p-3 font-medium text-gray-700">退款原因</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    <tr
                      v-for="item in (currentOrderDetail as any).refundList || []"
                      :key="item.orderRefundId"
                    >
                      <td class="p-3 text-left font-medium">{{ item.refundNo }}</td>
                      <td class="p-3 text-center text-gray-600">
                        {{ item.refundMethodName || '—' }}
                      </td>
                      <td class="p-3 text-center text-red-600">
                        ¥{{ (item.amount || 0).toFixed(2) }}
                      </td>
                      <td class="p-3 text-center">
                        <span
                          class="font-medium"
                          :class="getRefundDetailStatusTextColor(item.refundStatus)"
                        >
                          {{ getRefundDetailStatusText(item.refundStatus) }}
                        </span>
                      </td>
                      <td class="p-3 text-center text-gray-600">
                        {{ item.refundTime || '—' }}
                      </td>
                      <td class="p-3 text-left text-gray-600">
                        {{ item.refundReason || '—' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <OrderInvoiceDetails
            v-show="detailTab === 'invoice'"
            :order-no="currentOrderDetail.orderNo"
            :shop-id="currentOrderDetail.shopId"
            :active="detailTab === 'invoice'"
          />

          <div class="flex gap-3 mt-6 pt-6 border-t">
            <button
              v-if="canGenerateInvoice(currentOrderDetail)"
              class="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors"
              @click="openInvoiceModal(currentOrderDetail)"
            >
              <i class="fas fa-file-invoice mr-2"></i>开具发票
            </button>
            <!-- 打印订单按钮已注释
            <button
              @click="printOrder"
              class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <i class="fas fa-print mr-2"></i>打印订单
            </button>
            -->
            <button
              v-if="canPrintReceipt(currentOrderDetail)"
              class="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg transition-colors"
              @click="printReceipt(currentOrderDetail)"
            >
              <i class="fas fa-receipt mr-2"></i>收银小票
            </button>
            <button
              v-if="canPrintPickLabel(currentOrderDetail)"
              class="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg transition-colors"
              @click="printPickLabel(currentOrderDetail)"
            >
              <i class="fas fa-tag mr-2"></i>出餐标签 ({{
                currentOrderDetail.printOrderFood ?? '-'
              }})
            </button>
            <button
              class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
              @click="closeOrderDetail"
            >
              关闭
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 开票模态框 -->
    <div
      v-if="showInvoiceModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      @click="closeInvoiceModal"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full" @click.stop>
        <div class="bg-green-500 text-white px-6 py-4 flex items-center justify-between">
          <h3 class="text-xl font-semibold">开具发票</h3>
          <button class="text-white hover:text-gray-200" @click="closeInvoiceModal">
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>
        <div class="p-6">
          <form @submit.prevent="generateInvoice">
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">发票类型</label>
              <select
                v-model="invoiceForm.type"
                class="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="personal">个人发票</option>
                <option value="company">企业发票</option>
              </select>
            </div>
            <!-- 发票抬头：个人和企业都必填 -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >发票抬头 <span class="text-red-500">*</span></label
              >
              <input
                v-model="invoiceForm.companyName"
                type="text"
                class="w-full border border-gray-300 rounded-lg px-3 py-2"
                :placeholder="
                  invoiceForm.type === 'company' ? '请输入单位名称' : '请输入姓名或抬头'
                "
                required
              />
            </div>
            <!-- 企业税号：仅企业类型时必填 -->
            <div v-if="invoiceForm.type === 'company'" class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">税号</label>
              <input
                v-model="invoiceForm.taxNumber"
                type="text"
                class="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="请输入15-30位大写英文或数字"
                maxlength="30"
                required
              />
            </div>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >接收邮箱 <span class="text-red-500">*</span></label
              >
              <input
                v-model="invoiceForm.emailAddress"
                type="email"
                class="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="请输入接收发票的邮箱地址"
                required
              />
            </div>
            <div class="flex gap-3">
              <button
                type="submit"
                class="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <i class="fas fa-file-invoice mr-2"></i>开具发票
              </button>
              <button
                type="button"
                class="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                @click="closeInvoiceModal"
              >
                取消
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- 取消订单弹层 -->
    <div
      v-if="cancelOrderModalVisible && currentCancelOrder"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      @click.self="closeCancelOrderModal"
    >
      <div class="bg-white rounded-xl p-6 w-full max-w-md">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">取消订单</h3>
          <button
            class="text-gray-500 hover:text-gray-700"
            :disabled="isCancellingOrder"
            @click="closeCancelOrderModal"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <p class="text-gray-600 mb-4">
          确定要取消订单 #{{ currentCancelOrder.orderNo }} 吗？取消后将无法恢复。
        </p>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">选择取消原因（可选）</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="reason in cancelReasonOptions"
              :key="reason"
              type="button"
              class="px-4 py-2 rounded-lg border transition-colors"
              :class="
                cancelRemark === reason
                  ? 'border-red-500 bg-red-50 text-red-600'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              "
              @click="cancelRemark = reason"
            >
              {{ reason }}
            </button>
          </div>
        </div>

        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">取消原因（可选）</label>
          <textarea
            v-model="cancelRemark"
            rows="3"
            maxlength="200"
            placeholder="请输入取消原因，也可以不填直接取消"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
          ></textarea>
        </div>

        <div class="flex gap-3">
          <button
            type="button"
            class="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            :disabled="isCancellingOrder"
            @click="closeCancelOrderModal"
          >
            返回
          </button>
          <button
            type="button"
            class="flex-1 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-400"
            :disabled="isCancellingOrder"
            @click="confirmCancelOrder"
          >
            <span v-if="isCancellingOrder">
              <i class="fas fa-spinner fa-spin mr-2"></i>取消中
            </span>
            <span v-else>确认取消</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 支付确认模态框（与新建订单页保持一致） -->
    <div
      v-if="paymentConfirmModalVisible && currentPaymentOrder"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    >
      <div class="bg-white rounded-xl p-6 w-96 max-w-md max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">选择支付方式</h3>
          <button class="text-gray-500 hover:text-gray-700" @click="hidePaymentConfirmModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- 订单信息 -->
        <div class="mb-4 p-3 bg-gray-50 rounded-lg">
          <div class="flex justify-between items-center mb-2">
            <span class="text-gray-600">订单号:</span>
            <span class="font-medium">{{ currentPaymentOrder.orderNo }}</span>
          </div>
          <div class="flex justify-between items-center mb-2">
            <span class="text-gray-600">餐厅:</span>
            <span class="font-medium">{{ currentPaymentOrder.shopName }}</span>
          </div>
          <div class="flex justify-between items-center mb-2">
            <span class="text-gray-600">桌号:</span>
            <span class="font-medium">{{ currentPaymentOrder.tableName }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-gray-600">应付金额:</span>
            <span class="text-xl font-bold text-red-500">¥{{ currentPayAmount.toFixed(2) }}</span>
          </div>
        </div>

        <div class="space-y-4">
          <!-- 支付方式选择 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"> 支付方式 </label>
            <select
              v-model="selectedPaymentMethod"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
            >
              <option v-for="method in paymentMethods" :key="method.value" :value="method.value">
                {{ method.name }}
              </option>
            </select>
          </div>

          <!-- 备注输入 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"> 备注信息 </label>
            <textarea
              v-model="paymentRemark"
              placeholder="请输入备注信息（可选）"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base resize-none"
            ></textarea>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="flex space-x-3 pt-6 mt-6 border-t">
          <button
            class="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            @click="hidePaymentConfirmModal"
          >
            取消
          </button>
          <button
            class="flex-1 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
            @click="handlePaymentConfirm"
          >
            确认支付
          </button>
        </div>
      </div>
    </div>

    <!-- 扫码支付全屏界面（B扫C） -->
    <div v-if="scanPaymentModalVisible" class="fixed inset-0 bg-black z-50 flex flex-col">
      <!-- 顶部导航栏 -->
      <div class="bg-black bg-opacity-80 text-white px-4 py-3 flex items-center justify-between">
        <button class="text-white" @click="hideScanPaymentModal">
          <i class="fas fa-times text-2xl"></i>
        </button>
        <h3 class="text-xl font-bold">B扫C收款</h3>
        <div class="w-8"></div>
      </div>

      <!-- 摄像头扫描区域 -->
      <div class="flex-1 relative overflow-hidden w-full h-full">
        <div id="qr-reader" class="qr-reader-container w-full h-full"></div>

        <!-- 动态扫描线 -->
        <div v-if="isScanning" class="absolute inset-0 pointer-events-none z-20">
          <div class="scan-line"></div>
        </div>
      </div>

      <!-- 底部状态提示 -->
      <div v-if="scanStatus" class="bg-black bg-opacity-80 px-4 py-3">
        <div class="p-3 rounded-lg text-center" :class="scanStatusClass">
          <p class="text-sm font-medium">{{ scanStatus }}</p>
        </div>
      </div>
    </div>

    <!-- 扫码支付失败确认模态框 -->
    <div
      v-if="scanPaymentFailModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-xl p-6 w-96 max-w-md">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold">支付确认</h3>
          <button class="text-gray-500 hover:text-gray-700" @click="cancelScanPaymentConfirm">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="mb-6">
          <p class="text-gray-700 text-lg"> 扫码支付返回失败，请确认客户是否已经完成支付？ </p>
          <p v-if="scanPaymentFailErrorMsg" class="text-red-500 text-sm mt-2">
            错误信息：{{ scanPaymentFailErrorMsg }}
          </p>
        </div>

        <!-- 操作按钮 -->
        <div class="flex space-x-3">
          <button
            class="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            @click="cancelScanPaymentConfirm"
          >
            取消
          </button>
          <button
            :disabled="isRefreshingPayment"
            :class="[
              'flex-1 py-3 rounded-lg transition-colors font-medium',
              isRefreshingPayment
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-green-500 text-white hover:bg-green-600',
            ]"
            @click="confirmScanPaymentPaid"
          >
            <span v-if="isRefreshingPayment">
              <i class="fas fa-spinner fa-spin mr-2"></i>确认支付中
            </span>
            <span v-else>确认已经支付</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 申请退款模态框 -->
    <div
      v-if="showRefundModal"
      class="refund-modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click="closeRefundModal"
    >
      <div
        class="refund-modal-panel bg-white rounded-lg shadow-xl w-full overflow-hidden flex flex-col"
        @click.stop
      >
        <div class="bg-yellow-500 text-white px-6 py-4 flex items-center justify-between shrink-0">
          <h3 class="text-xl font-semibold">申请退款</h3>
          <button class="text-white hover:text-gray-200" @click="closeRefundModal">
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>
        <div v-if="currentRefundOrder" class="refund-modal-body p-6 overflow-y-auto flex-1 min-h-0">
          <div class="mb-4 p-3 bg-gray-50 rounded-lg">
            <div class="flex justify-between items-center mb-2">
              <span class="text-gray-600">订单号:</span>
              <span class="font-medium">{{ currentRefundOrder.orderNo }}</span>
            </div>
            <div class="flex justify-between items-center mb-2">
              <span class="text-gray-600">订单金额:</span>
              <span class="font-medium"
                >¥{{ (currentRefundOrder.totalAmount || 0).toFixed(2) }}</span
              >
            </div>
            <div class="flex justify-between items-center">
              <span class="text-gray-600">已支付金额:</span>
              <span class="font-medium"
                >¥{{ (currentRefundOrder.paymentAmount || 0).toFixed(2) }}</span
              >
            </div>
          </div>
          <form id="refund-form" @submit.prevent="submitRefund">
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                退款类型 <span class="text-red-500">*</span>
              </label>
              <div class="flex gap-6">
                <label class="flex items-center cursor-pointer">
                  <input v-model="refundForm.refundType" type="radio" :value="1" class="mr-2" />
                  退款退货
                </label>
                <label class="flex items-center cursor-pointer">
                  <input v-model="refundForm.refundType" type="radio" :value="2" class="mr-2" />
                  仅退款
                </label>
              </div>
            </div>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                选择退款菜品 <span class="text-red-500">*</span>
              </label>
              <div v-if="isLoadingRefundDetail" class="py-8 text-center text-gray-500">
                <i class="fas fa-spinner fa-spin mr-2"></i>正在加载菜品
              </div>
              <div
                v-else
                class="refund-food-scroll overflow-x-auto border border-gray-200 rounded-lg"
              >
                <table class="w-full min-w-[760px] text-sm">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="p-3 text-center">选择</th>
                      <th class="p-3 text-left">菜品</th>
                      <th class="p-3 text-center">原数量</th>
                      <th class="p-3 text-center">已退</th>
                      <th class="p-3 text-center">可退</th>
                      <th class="p-3 text-center">退款数量</th>
                      <th class="p-3 text-right">可退金额</th>
                      <th class="p-3 text-right">本次退款</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    <tr
                      v-for="item in refundFoodSelections"
                      :key="item.orderFoodId"
                      :class="item.disabled ? 'bg-gray-50 text-gray-400' : ''"
                    >
                      <td class="p-3 text-center">
                        <input
                          v-model="item.selected"
                          type="checkbox"
                          :disabled="item.disabled"
                          class="h-4 w-4"
                        />
                      </td>
                      <td class="p-3 text-left">
                        <div class="font-medium">{{ item.foodName }}</div>
                        <div class="text-xs text-gray-500">{{ item.foodSpecName }}</div>
                      </td>
                      <td class="p-3 text-center">{{ item.originalQuantity }}</td>
                      <td class="p-3 text-center">{{ item.refundedQuantity }}</td>
                      <td class="p-3 text-center">{{ item.remainingQuantity }}</td>
                      <td class="p-3 text-center">
                        <input
                          v-model.number="item.selectedQuantity"
                          type="number"
                          step="1"
                          min="1"
                          :max="item.remainingQuantity"
                          :disabled="item.disabled || !item.selected"
                          class="w-20 border border-gray-300 rounded px-2 py-1 text-center disabled:bg-gray-100"
                        />
                      </td>
                      <td class="p-3 text-right">¥{{ item.refundableAmount.toFixed(2) }}</td>
                      <td class="p-3 text-right font-medium text-yellow-600">
                        ¥{{ getSelectedRefundAmount(item).toFixed(2) }}
                      </td>
                    </tr>
                    <tr v-if="refundFoodSelections.length === 0">
                      <td colspan="8" class="p-6 text-center text-gray-500">暂无可退款菜品</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="mb-4 flex justify-between items-center rounded-lg bg-yellow-50 p-3">
              <span class="font-medium text-gray-700">本次退款总金额</span>
              <span class="text-xl font-semibold text-yellow-600">
                ¥{{ selectedRefundAmount.toFixed(2) }}
              </span>
            </div>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >退款原因 <span class="text-red-500">*</span></label
              >
              <textarea
                v-model="refundForm.refundReason"
                rows="4"
                class="refund-reason w-full border border-gray-300 rounded-lg px-3 py-2 resize-none"
                placeholder="请输入退款原因"
                required
              ></textarea>
            </div>
          </form>
        </div>
        <div v-if="currentRefundOrder" class="flex gap-3 border-t bg-white px-6 py-4 shrink-0">
          <button
            type="submit"
            form="refund-form"
            class="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg transition-colors"
            :disabled="isSubmittingRefund || isLoadingRefundDetail"
          >
            <span v-if="isSubmittingRefund">
              <i class="fas fa-spinner fa-spin mr-2"></i>提交中
            </span>
            <span v-else> <i class="fas fa-check mr-2"></i>确认申请 </span>
          </button>
          <button
            type="button"
            class="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
            @click="closeRefundModal"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, nextTick } from 'vue';
  import { useRouter } from 'vue-router';
  import { restoreSelectedShop } from '@/utils/selectedShop';
  import { fetchShopList } from '@/service/base';
  import {
    getOrderList,
    getOrder,
    insertOrderPayment,
    insertOrderInvoice,
    cancelOrder,
    orderPrint,
    orderSlipPrint,
    bookOrderSlipPrint,
    deliveryOrderSlipPrint,
    orderFoodPrint,
    insertOrderRefund,
    orderRefundComplete,
  } from '@/service/order';
  import type { FoodList, OrderDetailModel, OrderListItemModel, PaymentList } from '@/model/order';
  import { OrderPaymentModel } from '@/model/orderPayment';
  import { dateTimeFormat } from '@/utils/utils';
  import dayjs from 'dayjs';
  import { showDialog } from 'vant';
  import { scanPayment, refreshScanPayment } from '@/service/payment';
  import { Html5Qrcode } from 'html5-qrcode';
  import { useCurrentEmployee } from '@/composables/useCurrentEmployee';
  import OrderInvoiceDetails from '@/components/OrderInvoiceDetails.vue';

  const router = useRouter();
  const { employeeName } = useCurrentEmployee();

  const visibleFoodSpec = (dish: FoodList): string => {
    const spec = dish.foodSpecName?.trim();
    return spec && spec !== '标准' ? spec : '';
  };

  const foodDisplayName = (dish: FoodList): string => {
    const name = dish.foodName || dish.shopFoodName || '';
    const spec = visibleFoodSpec(dish);
    return `${name}${spec ? `（${spec}）` : ''}`;
  };

  const formatFoodSummary = (dish: FoodList): string => {
    const cookDesc = dish.foodCookDesc?.trim();
    return `${foodDisplayName(dish)}×${dish.quantity}${cookDesc ? `（${cookDesc}）` : ''}`;
  };

  // 接口定义
  interface InvoiceForm {
    type: 'personal' | 'company';
    companyName: string;
    taxNumber: string;
    emailAddress: string;
  }

  const taxNumberPattern = /^[A-Z0-9]{15,30}$/;
  const emailAddressPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  interface RefundDetailItem {
    orderRefundId?: number;
    refundNo?: string;
    refundMethodName?: string;
    amount?: number;
    refundStatus?: number;
    refundTime?: string | Date | null;
    refundReason?: string;
    foodList?: Array<{
      orderFoodId?: number;
      quantity?: number;
    }>;
  }

  interface OrderDetailExtra {
    refundList?: RefundDetailItem[];
    isRefund?: number;
    refundAmount?: number;
    refundStatus?: number;
    pickCode?: string;
  }

  interface RefundFoodSelection {
    orderFoodId: number;
    line: number;
    foodName: string;
    foodSpecName: string;
    originalQuantity: number;
    refundedQuantity: number;
    remainingQuantity: number;
    selectedQuantity: number;
    price: number;
    refundableAmount: number;
    selected: boolean;
    disabled: boolean;
  }

  // 响应式数据
  const loading = ref(false);
  const shopList = ref<any[]>([]);
  const tableList = ref<any[]>([]);
  const orderList = ref<OrderListItemModel[]>([]);
  const currentPage = ref(1);
  const pageSize = 50;
  const totalRecords = ref(0);
  const showOrderDetail = ref(false);
  const showInvoiceModal = ref(false);
  const cancelOrderModalVisible = ref(false);
  const currentCancelOrder = ref<OrderListItemModel | null>(null);
  const cancelRemark = ref('');
  const isCancellingOrder = ref(false);
  const cancelReasonOptions = ['用户取消', '重复下单'];
  // 详情数据兼容详情接口里的扩展字段，如 refundList / pickCode
  const currentOrderDetail = ref<(OrderDetailModel & OrderDetailExtra) | null>(null);

  // 支付相关状态（与新建订单页对齐）
  const paymentConfirmModalVisible = ref(false);
  const selectedPaymentMethod = ref(4); // 默认POS支付
  const paymentRemark = ref('');
  const currentPaymentOrder = ref<OrderListItemModel | null>(null);
  const currentOrderId = ref<number | null>(null);
  const currentOrderNo = ref('');

  const detailTab = ref<'food' | 'coupon' | 'payment' | 'refund' | 'invoice'>('food');

  const currentOrderCoupons = computed(() => {
    const foodList = Array.isArray(currentOrderDetail.value?.foodList)
      ? (currentOrderDetail.value?.foodList as FoodList[])
      : [];

    return foodList.flatMap((dish) => {
      const couponList = Array.isArray(dish.couponList) ? dish.couponList : [];
      return couponList.map((coupon) => ({
        ...coupon,
        sourceFoodName: dish.foodName || dish.shopFoodName || '-',
        sourceFoodSpecName: dish.foodSpecName || '标准',
      }));
    });
  });

  // 扫码支付相关状态
  const scanPaymentModalVisible = ref(false);
  const isScanning = ref(false);
  const scanPaymentRemark = ref('');
  const scanStatus = ref('');
  const scanStatusClass = ref('');
  const scanPaymentFailModalVisible = ref(false);
  const scanPaymentFailErrorMsg = ref('');
  const isRefreshingPayment = ref(false);
  const refreshingPaymentRecordKey = ref<string | null>(null);
  let html5QrCode: Html5Qrcode | null = null;

  // 退款相关状态
  const showRefundModal = ref(false);
  const currentRefundOrder = ref<OrderListItemModel | null>(null);
  const isSubmittingRefund = ref(false);
  const completingRefundOrderIds = ref<number[]>([]);
  const isLoadingRefundDetail = ref(false);
  const refundFoodSelections = ref<RefundFoodSelection[]>([]);
  const refundForm = ref({
    refundType: 1,
    refundReason: '',
  });

  const roundMoney = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

  const getSelectedRefundAmount = (item: RefundFoodSelection) => {
    if (!item.selected || item.selectedQuantity <= 0 || item.remainingQuantity <= 0) return 0;
    if (item.selectedQuantity >= item.remainingQuantity) return item.refundableAmount;
    return roundMoney((item.refundableAmount / item.remainingQuantity) * item.selectedQuantity);
  };

  const selectedRefundAmount = computed(() =>
    roundMoney(
      refundFoodSelections.value.reduce((total, item) => total + getSelectedRefundAmount(item), 0),
    ),
  );

  const filters = ref({
    startDate: '',
    endDate: '',
    table: '',
    restaurant: '',
    status: '',
    paymentStatus: '',
    refundStatus: '',
    printOrder: '',
    printOrderSlip: '',
    orderNo: '',
  });

  // 与桌位管理 / 厨房出餐统一使用的本地存储 key
  const SELECTED_SHOP_KEY = 'table_manage_selected_shop';

  // 保存选中的餐厅到 localStorage（如果后续需要在本页内变更）
  const saveSelectedShop = (shopId: number | string) => {
    try {
      localStorage.setItem(SELECTED_SHOP_KEY, String(shopId));
    } catch (e) {
      console.warn('保存选中餐厅失败:', e);
    }
  };

  const invoiceForm = ref<InvoiceForm>({
    type: 'personal',
    companyName: '',
    taxNumber: '',
    emailAddress: '',
  });

  // 当前支付订单应付金额（优先剩余金额）
  const currentPayAmount = computed(() => {
    if (!currentPaymentOrder.value) return 0;
    if (currentPaymentOrder.value.remainAmount && currentPaymentOrder.value.remainAmount > 0) {
      return currentPaymentOrder.value.remainAmount;
    }
    return currentPaymentOrder.value.totalAmount || 0;
  });

  // 当前订单菜品总份数
  const currentFoodCount = computed(() => {
    if (!currentPaymentOrder.value || !currentPaymentOrder.value.foodList) return 0;
    return currentPaymentOrder.value.foodList.reduce(
      (sum, dish: any) => sum + (dish.quantity || 0),
      0,
    );
  });

  // 当前订单是否支持线上支付
  const isOnlinePaymentCurrent = computed(() => {
    return !!(currentPaymentOrder.value && isOnlinePayment(currentPaymentOrder.value));
  });

  // 支付方式列表（与新建订单保持一致）
  const paymentMethods = computed(() => {
    const methods = [
      { value: 4, name: 'POS支付' },
      { value: 5, name: '公司挂账' },
      { value: 7, name: '现金支付' },
    ];
    if (isOnlinePaymentCurrent.value) {
      methods.unshift({ value: 3, name: 'B扫C收款' });
    }
    return methods;
  });

  // 从登录态获取员工姓名
  const getEmployeeName = () => {
    return employeeName.value;
  };

  // 计算属性 - 可用桌位列表（根据选择的餐厅筛选）
  const availableTables = computed(() => {
    if (!filters.value.restaurant) {
      return [];
    }
    return tableList.value.filter((table) => table.shopId === Number(filters.value.restaurant));
  });

  const totalFoodCount = computed(() => {
    return orderList.value.reduce((total, order) => {
      const foodCount = Array.isArray(order.foodList)
        ? order.foodList.reduce((sum, dish) => sum + (dish.quantity || 0), 0)
        : 0;
      return total + foodCount;
    }, 0);
  });

  const totalPages = computed(() => Math.max(1, Math.ceil(totalRecords.value / pageSize)));

  const visiblePages = computed(() => {
    const startPage = Math.max(1, Math.min(currentPage.value - 2, totalPages.value - 4));
    const endPage = Math.min(totalPages.value, startPage + 4);
    return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
  });

  // 加载餐厅列表
  const loadShopList = async () => {
    try {
      const response = await fetchShopList({
        pageNum: 1,
        pageSize: 100,
        data: { shopStatus: 0 },
      });
      if (response.code === 200) {
        shopList.value = response.result.list;

        const shopId = restoreSelectedShop(shopList.value);
        filters.value.restaurant = shopId ? String(shopId) : '';
        if (shopId) await loadTableList(shopId);
      }
    } catch (error) {
      console.error('加载餐厅列表失败:', error);
      showToast('加载餐厅列表失败');
    }
  };

  // 加载桌位列表 - 使用真实的桌位接口
  const loadTableList = async (shopId?: number) => {
    if (!shopId) {
      tableList.value = [];
      return;
    }
    try {
      // 调用真实的桌位接口
      const params: any = {
        pageNum: 1,
        pageSize: 999,
        data: {},
      };

      if (shopId) {
        params.data.shopId = shopId;
      }

      // 使用post方法直接调用桌位接口
      const { post } = await import('@/utils/api');
      const response = await post<any>('/dim/table/select', params);

      if (response.code === 200) {
        tableList.value = response.result.list || [];
      } else {
        // 如果接口失败，使用模拟数据作为备选
        const mockTables = [
          { tableId: 1, tableName: '1号桌', shopId: 1 },
          { tableId: 2, tableName: '2号桌', shopId: 1 },
          { tableId: 3, tableName: '3号桌', shopId: 1 },
          { tableId: 4, tableName: '4号桌', shopId: 2 },
          { tableId: 5, tableName: '5号桌', shopId: 2 },
          { tableId: 6, tableName: '6号桌', shopId: 2 },
          { tableId: 7, tableName: '7号桌', shopId: 3 },
          { tableId: 8, tableName: '8号桌', shopId: 3 },
          { tableId: 9, tableName: '9号桌', shopId: 4 },
          { tableId: 10, tableName: '10号桌', shopId: 4 },
          { tableId: 11, tableName: '11号桌', shopId: 5 },
          { tableId: 12, tableName: '12号桌', shopId: 5 },
        ];

        if (shopId) {
          tableList.value = mockTables.filter((table) => table.shopId === shopId);
        } else {
          tableList.value = mockTables;
        }
      }
    } catch (error) {
      console.error('加载桌位列表失败:', error);
      showToast('加载桌位列表失败');

      // 出错时使用模拟数据
      const mockTables = [
        { tableId: 1, tableName: '1号桌', shopId: 1 },
        { tableId: 2, tableName: '2号桌', shopId: 1 },
        { tableId: 3, tableName: '3号桌', shopId: 1 },
        { tableId: 4, tableName: '4号桌', shopId: 2 },
        { tableId: 5, tableName: '5号桌', shopId: 2 },
        { tableId: 6, tableName: '6号桌', shopId: 2 },
        { tableId: 7, tableName: '7号桌', shopId: 3 },
        { tableId: 8, tableName: '8号桌', shopId: 3 },
        { tableId: 9, tableName: '9号桌', shopId: 4 },
        { tableId: 10, tableName: '10号桌', shopId: 4 },
        { tableId: 11, tableName: '11号桌', shopId: 5 },
        { tableId: 12, tableName: '12号桌', shopId: 5 },
      ];

      if (shopId) {
        tableList.value = mockTables.filter((table) => table.shopId === shopId);
      } else {
        tableList.value = mockTables;
      }
    }
  };

  // 加载订单列表
  const loadOrderList = async () => {
    if (!filters.value.restaurant) {
      orderList.value = [];
      totalRecords.value = 0;
      showToast('请选择餐厅');
      return;
    }
    try {
      loading.value = true;

      const params: any = {
        pageNum: currentPage.value,
        pageSize,
        data: {},
      };

      // 构建筛选条件
      if (filters.value.restaurant) {
        params.data.shopId = Number(filters.value.restaurant);
      }
      if (filters.value.table) {
        params.data.tableId = Number(filters.value.table);
      }
      if (filters.value.status) {
        params.data.orderStatuss = [Number(filters.value.status)];
      }
      if (filters.value.paymentStatus) {
        params.data.paymentStatuss = [Number(filters.value.paymentStatus)];
      }
      if (filters.value.refundStatus) {
        params.data.refundStatus = Number(filters.value.refundStatus);
      }
      if (filters.value.printOrder !== '') {
        params.data.printOrder = Number(filters.value.printOrder);
      }
      if (filters.value.printOrderSlip !== '') {
        params.data.printOrderSlip = Number(filters.value.printOrderSlip);
      }
      if (filters.value.orderNo && String(filters.value.orderNo).trim()) {
        params.data.orderNo = String(filters.value.orderNo).trim();
      }
      if (filters.value.startDate && filters.value.endDate) {
        // 设置时间范围
        const startDate = new Date(filters.value.startDate);
        const endDate = new Date(filters.value.endDate);

        // 开始日期设置为 00:00:00.000
        startDate.setHours(0, 0, 0, 0);
        // 结束日期设置为 23:59:59.999
        endDate.setHours(23, 59, 59, 999);

        // 格式化为 "YYYY-MM-DD HH:mm:ss" 格式
        const formatDateTime = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };

        params.createtimeBegin = formatDateTime(startDate);
        params.createtimeEnd = formatDateTime(endDate);
      } else if (filters.value.startDate) {
        // 只有开始日期，设置为当天
        const startDate = new Date(filters.value.startDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(filters.value.startDate);
        endDate.setHours(23, 59, 59, 999);

        const formatDateTime = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };

        params.createtimeBegin = formatDateTime(startDate);
        params.createtimeEnd = formatDateTime(endDate);
      }

      const response = await getOrderList(params);
      if (response.code === 200) {
        orderList.value = response.result.list || [];
        totalRecords.value = response.result.total || 0;
      } else {
        showToast(response.msg || '加载订单列表失败');
      }
    } catch (error) {
      console.error('加载订单列表失败:', error);
      showToast('加载订单列表失败');
    } finally {
      loading.value = false;
    }
  };

  const refreshOrderListPreservingScroll = async () => {
    const scrollPosition = window.scrollY;
    await loadOrderList();
    await nextTick();
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          window.scrollTo({ top: scrollPosition, behavior: 'auto' });
          resolve();
        });
      });
    });
  };

  // 餐厅选择变化事件
  const onRestaurantChange = () => {
    // 清空桌号选择
    filters.value.table = '';
    // 如果选择了具体餐厅，加载该餐厅的桌位列表
    if (filters.value.restaurant) {
      // 保存到本地存储，便于其他页面复用
      saveSelectedShop(filters.value.restaurant);
      loadTableList(Number(filters.value.restaurant));
    }
  };

  // 计算属性
  const getStatusColor = (status?: number) => {
    const colors = {
      1: 'bg-yellow-100 text-yellow-800', // 待确认
      2: 'bg-blue-100 text-blue-800', // 待出餐
      8: 'bg-green-100 text-green-800', // 已出餐
      10: 'bg-red-100 text-red-800', // 已完成
      999: 'bg-purple-100 text-purple-800', // 已取消
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusColor = (paymentStatusName?: string) => {
    if (!paymentStatusName) return 'bg-gray-100 text-gray-800';

    const statusLower = paymentStatusName.toLowerCase();
    if (statusLower.includes('已支付') || statusLower.includes('支付成功')) {
      return 'bg-green-500 text-white'; // 深绿色背景，白色文字
    } else if (statusLower.includes('未支付') || statusLower.includes('待支付')) {
      return 'bg-red-500 text-white'; // 深红色背景，白色文字
    } else if (statusLower.includes('支付中') || statusLower.includes('处理中')) {
      return 'bg-yellow-500 text-white'; // 黄色背景，白色文字
    } else if (statusLower.includes('退款') || statusLower.includes('已退款')) {
      return 'bg-purple-500 text-white'; // 深紫色背景，白色文字
    }
    return 'bg-gray-500 text-white'; // 默认灰色背景，白色文字
  };

  const getStatusText = (status?: number) => {
    const statusMap = {
      1: '已点餐',
      2: '待出餐',
      8: '已出餐',
      10: '已完成',
      999: '已取消',
    };
    return statusMap[status as keyof typeof statusMap] || '未知状态';
  };

  const getDineTypeText = (dineType?: number) => {
    const dineTypeMap = {
      1: '堂食',
      2: '自提',
      3: '外卖',
      4: '打包',
      5: '预定',
    };
    return dineTypeMap[dineType as keyof typeof dineTypeMap] || '';
  };

  const getDineTypeColor = (dineType?: number) => {
    const colors = {
      1: 'bg-emerald-100 text-emerald-700',
      2: 'bg-amber-100 text-amber-700',
      3: 'bg-cyan-100 text-cyan-700',
      4: 'bg-gray-100 text-gray-700',
      5: 'bg-purple-100 text-purple-700',
    };
    return colors[dineType as keyof typeof colors] || 'bg-gray-100 text-gray-700';
  };

  const getStatusTextColor = (status?: number) => {
    const colors = {
      1: 'text-yellow-600',
      2: 'text-blue-600',
      8: 'text-green-600',
      10: 'text-red-600',
      999: 'text-purple-600',
    };
    return colors[status as keyof typeof colors] || 'text-gray-600';
  };

  // 新增：获取订单层级退款状态文本（0：待退款，10：已退款）
  const getRefundStatusText = (status?: number) => {
    const statusNum = Number(status);
    if (statusNum === 0) return '待退款';
    if (statusNum === 10) return '已退款';
    return '未知状态';
  };

  // 新增：获取订单层级退款状态文本颜色
  const getRefundStatusTextColor = (status?: number) => {
    if (status === undefined || status === null) return 'text-gray-600';
    const statusNum = Number(status);
    if (statusNum === 0) return 'text-yellow-600'; // 待退款 - 黄色
    if (statusNum === 10) return 'text-green-600'; // 已退款 - 绿色
    return 'text-gray-600';
  };

  // 新增：获取退款明细行的退款状态文本（refundList 内部：0待退款，1退款成功，2退款失败）
  const getRefundDetailStatusText = (status?: number) => {
    const statusNum = Number(status);
    if (statusNum === 0) return '待退款';
    if (statusNum === 1) return '退款成功';
    if (statusNum === 2) return '退款失败';
    return '未知状态';
  };

  // 新增：获取退款明细行的退款状态颜色
  const getRefundDetailStatusTextColor = (status?: number) => {
    if (status === undefined || status === null) return 'text-gray-600';
    const statusNum = Number(status);
    if (statusNum === 0) return 'text-yellow-600'; // 待退款 - 黄色
    if (statusNum === 1) return 'text-green-600'; // 退款成功 - 绿色
    if (statusNum === 2) return 'text-red-600'; // 退款失败 - 红色
    return 'text-gray-600';
  };

  // 方法
  const formatDateTime = (dateTime?: string | Date) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  // 新增：判断是否是自提订单
  const isSelfPickupOrder = (order: OrderListItemModel | OrderDetailModel | null) => {
    if (!order) return false;
    return order.dineType === 2 || Boolean(order.tableTag && order.tableTag.includes('自提'));
  };

  const getDisplayPickCode = (order: OrderListItemModel | OrderDetailModel | null) => {
    if (!order) return '';
    if (isSelfPickupOrder(order)) {
      return order.orderNo?.slice(-4) || '';
    }
    return order.pickCode || '';
  };

  const isDeliveryOrder = (order: OrderListItemModel | OrderDetailModel | null) => {
    if (!order) return false;
    return order.dineType === 3;
  };

  // 新增：格式化自提时间显示
  const formatSelfPickupTime = (order: OrderListItemModel | OrderDetailModel | null) => {
    if (!order) return '';
    const date = order.selfPickDate?.trim() || '';
    const time = order.selfPickTime?.trim() || '';
    if (!date && !time) return '';
    if (date && time) return `${date} ${time}`;
    if (date) return date;
    if (time) return time;
    return '';
  };

  const appointmentTimeText = computed(() => formatSelfPickupTime(currentOrderDetail.value));

  const getPaymentRecordStatusText = (status?: number) => {
    if (status === 1) return '成功';
    if (status === 0) return '待支付';
    if (status === 2) return '失败';
    return status === undefined || status === null ? '-' : String(status);
  };

  const canRefreshFailedScanPayment = (payment: PaymentList): boolean => {
    const isScanPayment =
      payment.paymentMethod === 3 || payment.paymentMethodName?.includes('B扫C') === true;
    return isScanPayment && payment.paymentStatus === 2;
  };

  const getPaymentRecordKey = (payment: PaymentList): string =>
    String(payment.orderPaymentId ?? payment.paymentNo ?? payment.orderId ?? '');

  const refreshFailedScanPayment = async (payment: PaymentList) => {
    const orderId = payment.orderId ?? currentOrderDetail.value?.orderId;
    if (!orderId) {
      showDialog({ message: '订单信息不完整，无法查询支付结果' });
      return;
    }

    const paymentRecordKey = getPaymentRecordKey(payment);
    if (refreshingPaymentRecordKey.value === paymentRecordKey) return;

    try {
      refreshingPaymentRecordKey.value = paymentRecordKey;
      const response = await refreshScanPayment({ orderId });

      if (response.code === 200 && response.result?.success) {
        showDialog({ message: '支付成功！' });
        const detailResponse = await getOrder({ orderId });
        if (detailResponse.code === 200 && detailResponse.result) {
          currentOrderDetail.value = detailResponse.result;
        }
        await refreshOrderListPreservingScroll();
        return;
      }

      const errorMsg =
        response.result?.errorMsg || response.msg || '暂未查询到支付成功，请稍后重试';
      showDialog({ message: errorMsg });
    } catch (error) {
      console.error('重新查询扫码支付结果失败:', error);
      showDialog({ message: '查询支付结果失败，请稍后重试' });
    } finally {
      refreshingPaymentRecordKey.value = null;
    }
  };

  const getCouponStatusText = (status?: number) => {
    if (status === 1) return '未使用';
    if (status === 5) return '分享待领取';
    if (status === 6) return '分享已领取';
    if (status === 10) return '已使用';
    if (status === 999) return '已取消';
    return status === undefined || status === null ? '-' : String(status);
  };

  const getCouponStatusClass = (status?: number) => {
    if (status === 1) return 'text-green-600';
    if (status === 5) return 'text-yellow-600';
    if (status === 6) return 'text-blue-600';
    if (status === 10) return 'text-indigo-600';
    if (status === 999) return 'text-gray-500';
    return 'text-yellow-600';
  };

  const getCouponSourceTypeText = (sourceType?: number) => {
    if (sourceType === 1) return '购买';
    if (sourceType === 2) return '分享领取';
    return sourceType === undefined || sourceType === null ? '-' : String(sourceType);
  };

  const getCouponShareText = (isShare?: number) => {
    if (isShare === 1) return '可分享';
    if (isShare === 0) return '不可分享';
    return '-';
  };

  const getCouponDisabledText = (disabled?: number) => {
    if (disabled === 0) return '正常';
    if (disabled === 1) return '已删除';
    return '-';
  };

  const getCouponDisabledClass = (disabled?: number) => {
    if (disabled === 0) return 'text-green-600';
    if (disabled === 1) return 'text-red-600';
    return 'text-gray-500';
  };

  const applyFilters = () => {
    currentPage.value = 1;
    loadOrderList();
  };

  const clearFilters = () => {
    const { startDate, endDate, restaurant } = filters.value;
    filters.value = {
      startDate,
      endDate,
      table: '',
      restaurant,
      status: '',
      paymentStatus: '',
      refundStatus: '',
      printOrder: '',
      printOrderSlip: '',
      orderNo: '',
    };
    currentPage.value = 1;
    loadOrderList();
  };

  const changePage = async (page: number) => {
    if (page < 1 || page > totalPages.value || page === currentPage.value) return;
    currentPage.value = page;
    await loadOrderList();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const viewOrderDetail = async (order: OrderListItemModel) => {
    if (!order.orderId) {
      showToast('订单信息不完整，无法查看详情');
      return;
    }

    try {
      const response = await getOrder({ orderId: order.orderId });
      if (response.code === 200 && response.result) {
        // 使用详情接口返回的数据，包含 refundList 等字段
        currentOrderDetail.value = response.result;
        // 打开详情时，默认切回菜品明细 Tab
        detailTab.value = 'food';
        showOrderDetail.value = true;
      } else {
        showToast(response.msg || '加载订单详情失败');
      }
    } catch (error) {
      console.error('加载订单详情失败:', error);
      showToast('加载订单详情失败');
    }
  };

  const closeOrderDetail = () => {
    showOrderDetail.value = false;
    currentOrderDetail.value = null;
  };

  const openInvoiceModal = (order: OrderListItemModel) => {
    currentOrderDetail.value = order;
    showInvoiceModal.value = true;
  };

  const closeInvoiceModal = () => {
    showInvoiceModal.value = false;
    // 重置表单
    invoiceForm.value = {
      type: 'personal',
      companyName: '',
      taxNumber: '',
      emailAddress: '',
    };
  };

  const generateInvoice = async () => {
    if (!currentOrderDetail.value) return;

    // 验证发票抬头（个人和企业都必填）
    if (!invoiceForm.value.companyName || invoiceForm.value.companyName.trim() === '') {
      showToast('请填写发票抬头');
      return;
    }

    // 验证企业发票税号
    if (invoiceForm.value.type === 'company') {
      const taxNumber = invoiceForm.value.taxNumber.trim();
      if (!taxNumber) {
        showToast('请填写税号');
        return;
      }
      if (!taxNumberPattern.test(taxNumber)) {
        showToast('税号格式应为15-30位大写英文或数字组合');
        return;
      }
    }

    // 验证接收邮箱
    const emailAddress = invoiceForm.value.emailAddress.trim();
    if (!emailAddress) {
      showToast('请填写接收邮箱地址');
      return;
    }
    if (!emailAddressPattern.test(emailAddress)) {
      showToast('请输入正确的邮箱地址');
      return;
    }

    // 组装开票参数，调用后端开票接口
    try {
      const invoiceAmount = currentOrderDetail.value.totalAmount || 0;
      const payload = {
        shopId: currentOrderDetail.value.shopId,
        invoiceType: invoiceForm.value.type === 'company' ? 2 : 1,
        title: invoiceForm.value.companyName.trim(),
        taxNo: invoiceForm.value.type === 'company' ? invoiceForm.value.taxNumber.trim() : '',
        orderCount: 1,
        orderNos: [currentOrderDetail.value.orderNo],
        invoiceAmount,
        receiveType: 1,
        emailAddress,
      };

      const resp = await insertOrderInvoice(payload);
      if (resp.code === 200) {
        showDialog({
          message: `发票开具成功！\n订单号：${
            currentOrderDetail.value.orderNo
          }\n开票金额：¥${invoiceAmount.toFixed(
            2,
          )}\n发票将发送至：${invoiceForm.value.emailAddress.trim()}`,
        });
        // 关闭模态框并刷新订单列表（以更新 invoiceStatus）
        closeInvoiceModal();
        await refreshOrderListPreservingScroll();
      } else {
        showDialog({ message: resp.msg || '开具发票失败，请稍后重试' });
      }
    } catch (e) {
      console.error('开具发票失败:', e);
      showDialog({ message: '开具发票失败，请稍后重试' });
    }
  };

  const printInvoice = (invoiceData: any) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // 获取当前订单对应的餐厅信息
    const currentShop = shopList.value.find(
      (shop) => shop.shopId === currentOrderDetail.value?.shopId,
    );
    const shopName = currentShop?.shopName || '美食集市';
    const shopAddress = currentShop?.shopAddress || shopName;
    const contactPhone = currentShop?.contactPhone || '400-888-8888';

    printWindow.document.write(`
    <html>
      <head>
        <title>发票</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 14px; margin: 20px; }
          .invoice-header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
          .invoice-title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
          .invoice-number { font-size: 16px; color: #666; }
          .invoice-info { margin-bottom: 20px; }
          .invoice-info div { margin-bottom: 8px; }
          .invoice-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          .invoice-table th, .invoice-table td { border: 1px solid #000; padding: 8px; text-align: left; }
          .invoice-table th { background-color: #f0f0f0; font-weight: bold; }
          .invoice-footer { text-align: center; margin-top: 30px; font-size: 12px; color: #666; }
          .total-amount { font-size: 18px; font-weight: bold; text-align: center; margin: 20px 0; }
        </style>
      </head>
      <body>
        <div class="invoice-header">
          <div class="invoice-title">发票</div>
          <div class="invoice-number">发票号码: ${invoiceData.invoiceNumber}</div>
        </div>
        
        <div class="invoice-info">
          <div><strong>订单号:</strong> ${invoiceData.orderId}</div>
          <div><strong>发票类型:</strong> ${
            invoiceData.type === 'personal' ? '个人发票' : '企业发票'
          }</div>
          ${
            invoiceData.type === 'company'
              ? `
            <div><strong>企业名称:</strong> ${invoiceData.companyName}</div>
            <div><strong>税号:</strong> ${invoiceData.taxNumber}</div>
          `
              : ''
          }
          <div><strong>开票日期:</strong> ${invoiceData.issueDate}</div>
          <div><strong>发票内容:</strong> ${invoiceData.content}</div>
        </div>
        
        <table class="invoice-table">
          <thead>
            <tr>
              <th>项目</th>
              <th>金额</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${invoiceData.content}</td>
              <td>¥${invoiceData.amount.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        
        <div class="total-amount">
          合计金额: ¥${invoiceData.amount.toFixed(2)}
        </div>
        
        <div class="invoice-footer">
          <p>${shopName}</p>
          <p>地址: ${shopAddress} | 电话: ${contactPhone}</p>
          <p>开票时间: ${invoiceData.issueDate}</p>
        </div>
      </body>
    </html>
  `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  const printOrder = () => {
    if (!currentOrderDetail.value) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const orderTime = new Date(currentOrderDetail.value.createTime || '');

    // 获取当前订单对应的餐厅信息
    const currentShop = shopList.value.find(
      (shop) => shop.shopId === currentOrderDetail.value?.shopId,
    );
    const shopName = currentShop?.shopName || '美食集市';
    const shopAddress = currentShop?.shopAddress || '地址未知';
    const contactPhone = currentShop?.contactPhone || '400-888-8888';

    printWindow.document.write(`
    <html>
      <head>
        <title>订单详情</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 14px; margin: 20px; }
          .order-header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 10px; margin-bottom: 15px; }
          .order-title { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
          .order-item { display: flex; justify-content: space-between; margin-bottom: 8px; }
          .order-footer { border-top: 2px dashed #000; padding-top: 10px; margin-top: 15px; text-align: center; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="order-header">
          <div class="order-title">${shopName} - 订单详情</div>
          <div>订单号: ${currentOrderDetail.value.orderNo}</div>
        </div>
        
        <div class="order-item">
          <span>餐厅:</span>
          <span>${currentOrderDetail.value.shopName}</span>
        </div>
        <div class="order-item">
          <span>桌号:</span>
          <span>${currentOrderDetail.value.tableName}</span>
        </div>
        <div class="order-item">
          <span>用餐人数:</span>
          <span>${currentOrderDetail.value.personCount}人</span>
        </div>
        <div class="order-item">
          <span>服务员:</span>
          <span>${currentOrderDetail.value.userName || '服务员'}</span>
        </div>
        <div class="order-item">
          <span>下单时间:</span>
          <span>${orderTime.toLocaleString()}</span>
        </div>
        <div class="order-item">
          <span>订单状态:</span>
          <span>${getStatusText(currentOrderDetail.value.orderStatus)}</span>
        </div>
        
        <div style="margin: 15px 0; text-align: center; font-weight: bold;">菜品明细</div>
        
        ${(currentOrderDetail.value.foodList || [])
          .filter((dish) => {
            const status = dish.status ?? dish.foodStatus;
            return status !== 999;
          })
          .map(
            (dish) => `
          <div class="order-item">
            <span>${dish.foodName} x${dish.quantity}</span>
            <span>¥${((dish.price || 0) * (dish.quantity || 0)).toFixed(2)}</span>
          </div>
        `,
          )
          .join('')}
        
        <div class="order-footer">
          <div style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">
            总计: ¥${(currentOrderDetail.value.totalAmount || 0).toFixed(2)}
          </div>
          <div>${shopName}</div>
          <div>地址: ${shopAddress} | 电话: ${contactPhone}</div>
          <div>打印时间: ${new Date().toLocaleString()}</div>
        </div>
      </body>
    </html>
  `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  const goBack = () => {
    router.back();
  };

  const showToast = (message: string) => {
    const toast = document.createElement('div');
    toast.className =
      'fixed top-20 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg z-50';
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 2000);
  };

  // 新增：判断是否为未支付或部分支付订单
  const isUnpaidOrder = (order: OrderListItemModel) => {
    // 调试信息
    console.log('检查订单支付状态:', {
      orderNo: order.orderNo,
      paymentStatusName: order.paymentStatusName,
      orderStatus: order.orderStatus,
      totalAmount: order.totalAmount,
      paymentAmount: order.paymentAmount,
      remainAmount: order.remainAmount,
    });

    // 如果订单已取消或已完成且无待支付金额，不显示支付按钮
    if (order.orderStatus === 999) {
      return false; // 已取消
    }

    // 如果有remainAmount字段且大于0，说明有待支付金额
    if (order.remainAmount && order.remainAmount > 0) {
      return true;
    }

    // 如果没有支付状态信息，根据订单状态判断
    if (!order.paymentStatusName) {
      // 订单状态为已完成(10)时，如果没有待支付金额就不需要支付
      if (order.orderStatus === 10 && (!order.remainAmount || order.remainAmount <= 0)) {
        return false;
      }
      // 其他状态默认可以支付
      return true;
    }

    const statusLower = order.paymentStatusName.toLowerCase();
    // 扩展匹配条件：待支付和部分支付都显示支付按钮
    return (
      statusLower.includes('未支付') ||
      statusLower.includes('待支付') ||
      statusLower.includes('部分支付') ||
      statusLower.includes('partial') ||
      statusLower.includes('unpaid') ||
      statusLower.includes('pending') ||
      statusLower === '未付款' ||
      statusLower === '待付款'
    );
  };

  // 新增：判断是否为在线支付订单
  const isOnlinePayment = (order: OrderListItemModel) => {
    return order.shopPaymentLine === 1; // 1 表示线上支付
  };

  // 打开订单查询支付（与新建订单支付流程一致）
  const openHistoryPayment = (order: OrderListItemModel) => {
    if (!order.orderId) {
      showToast('订单信息不完整，无法支付');
      return;
    }
    currentPaymentOrder.value = order;
    currentOrderId.value = order.orderId;
    currentOrderNo.value = order.orderNo || '';

    // 默认支付方式：若支持B扫C则为3，否则为4
    selectedPaymentMethod.value = isOnlinePayment(order) ? 3 : 4;
    paymentRemark.value = '';
    paymentConfirmModalVisible.value = true;
  };

  const hidePaymentConfirmModal = () => {
    paymentConfirmModalVisible.value = false;
    paymentRemark.value = '';
    selectedPaymentMethod.value = isOnlinePaymentCurrent.value ? 3 : 4;
  };

  // 支付确认（选择支付方式后）
  const handlePaymentConfirm = () => {
    if (!currentPaymentOrder.value || !currentOrderId.value) {
      showDialog({ message: '订单信息不完整，无法确认支付' });
      return;
    }
    if (selectedPaymentMethod.value === 3) {
      // B扫C收款
      paymentConfirmModalVisible.value = false;
      showScanPaymentModal();
    } else {
      // 其他线下支付
      confirmPayment();
    }
  };

  // 线下支付确认（POS / 挂账 / 现金）
  const confirmPayment = async () => {
    try {
      if (!currentPaymentOrder.value || !currentOrderId.value) {
        showDialog({ message: '订单信息不完整，无法确认支付' });
        return;
      }

      const employeeName = getEmployeeName();
      let remark = '';
      let paymentMethodName = '';

      if (selectedPaymentMethod.value === 3) {
        paymentMethodName = 'B扫C收款';
        remark = `服务员${employeeName}确认B扫C收款完成，${paymentRemark.value || ''}`;
      } else if (selectedPaymentMethod.value === 4) {
        paymentMethodName = 'POS支付';
        remark = `服务员${employeeName}确认POS支付完成，${paymentRemark.value || ''}`;
      } else if (selectedPaymentMethod.value === 5) {
        paymentMethodName = '公司挂账';
        remark = `服务员${employeeName}确认挂账，${paymentRemark.value || ''}`;
      } else if (selectedPaymentMethod.value === 7) {
        paymentMethodName = '现金支付';
        remark = `服务员${employeeName}确认现金支付完成，${paymentRemark.value || ''}`;
      }

      const paymentData: OrderPaymentModel = {
        orderId: currentOrderId.value,
        paymentMethod: selectedPaymentMethod.value,
        paymentMethodName,
        paymentStatus: 1,
        amount: currentPayAmount.value,
        paymentTime: dateTimeFormat(dayjs()),
        remark: remark.trim(),
      };

      const response = await insertOrderPayment(paymentData);
      if (response.code === 200) {
        hidePaymentConfirmModal();
        showDialog({
          message: `订单 ${
            currentOrderNo.value
          } ${paymentMethodName}已确认完成！\n总金额：¥${currentPayAmount.value.toFixed(2)}\n共${
            currentFoodCount.value
          }份菜品`,
        });
        await refreshOrderListPreservingScroll();
      } else {
        showDialog({ message: response.msg || '确认支付失败' });
      }
    } catch (error) {
      console.error('确认支付失败:', error);
      showDialog({ message: '确认支付失败，请稍后重试' });
    }
  };

  // 显示扫码支付全屏界面
  const showScanPaymentModal = async () => {
    scanPaymentModalVisible.value = true;
    scanPaymentRemark.value = '';
    scanStatus.value = '';
    isScanning.value = true;
    await nextTick();
    getCamerasInfo();
  };

  // 隐藏扫码支付全屏界面
  const hideScanPaymentModal = () => {
    stopScan();
    scanPaymentModalVisible.value = false;
    scanPaymentRemark.value = '';
    scanStatus.value = '';
  };

  // 获取摄像头信息并初始化
  const getCamerasInfo = () => {
    isScanning.value = true;

    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length) {
          html5QrCode = new Html5Qrcode('qr-reader');
          startScan();
        } else {
          isScanning.value = false;
          scanStatus.value = '未检测到摄像头设备';
          scanStatusClass.value = 'bg-red-100 text-red-700';
        }
      })
      .catch((err) => {
        console.error('获取摄像头失败:', err);
        scanStatus.value = '摄像头无访问权限！';
        scanStatusClass.value = 'bg-red-100 text-red-700';
        isScanning.value = false;
      });
  };

  // 开始扫码
  const startScan = () => {
    if (!html5QrCode) {
      console.error('html5QrCode 未初始化');
      return;
    }

    isScanning.value = true;
    scanStatus.value = '';
    scanStatusClass.value = '';

    html5QrCode
      .start(
        { facingMode: 'environment' },
        {
          fps: 20,
          qrbox(viewfinderWidth: number, viewfinderHeight: number) {
            return {
              width: viewfinderWidth,
              height: viewfinderHeight,
            };
          },
          aspectRatio: undefined,
        },
        (decodedText: string) => {
          handleScanResult(decodedText);
        },
      )
      .catch((err: any) => {
        console.error('扫码失败', err);
        scanStatus.value = '启动摄像头失败，请检查权限设置';
        scanStatusClass.value = 'bg-red-100 text-red-700';
        isScanning.value = false;
      });
  };

  // 停止扫描
  const stopScan = () => {
    if (html5QrCode && html5QrCode.isScanning) {
      html5QrCode
        .stop()
        .then(() => {
          html5QrCode?.clear();
          isScanning.value = false;
        })
        .catch((err: any) => {
          console.error('停止扫描失败:', err);
          isScanning.value = false;
        });
    } else {
      isScanning.value = false;
    }
  };

  // 处理扫码结果
  const handleScanResult = async (scanCode: string) => {
    if (!scanCode || scanCode.trim() === '') return;
    stopScan();
    scanStatus.value = '扫码成功，正在处理支付...';
    scanStatusClass.value = 'bg-blue-100 text-blue-700';
    await submitScanPayment(scanCode);
  };

  // 提交扫码支付
  const submitScanPayment = async (scanCode: string) => {
    try {
      if (!currentOrderId.value || !currentPaymentOrder.value) {
        scanStatus.value = '订单信息不完整，无法支付';
        scanStatusClass.value = 'bg-red-100 text-red-700';
        showDialog({ message: '订单信息不完整，无法支付' });
        return;
      }

      const ip = await getClientIP();

      const paymentData = {
        orderId: currentOrderId.value,
        scanCode,
        remark: scanPaymentRemark.value || '',
        ip: ip || '',
        amount: currentPayAmount.value,
      };

      const response = await scanPayment(paymentData);
      if (response.code === 200) {
        hideScanPaymentModal();
        if (response.result?.success) {
          showDialog({
            message: `扫码支付成功！\n总金额：¥${currentPayAmount.value.toFixed(2)}\n共${
              currentFoodCount.value
            }份菜品`,
          });
          await refreshOrderListPreservingScroll();
        } else {
          scanPaymentFailErrorMsg.value = response.result?.errorMsg || '';
          scanPaymentFailModalVisible.value = true;
        }
      } else {
        const errorMsg = response.msg || '支付失败，请重试';
        scanStatus.value = errorMsg;
        scanStatusClass.value = 'bg-red-100 text-red-700';
        showDialog({ message: errorMsg });
        await nextTick();
        getCamerasInfo();
      }
    } catch (error) {
      console.error('扫码支付失败:', error);
      scanStatus.value = '支付失败，请稍后重试';
      scanStatusClass.value = 'bg-red-100 text-red-700';
      showDialog({ message: '支付失败，请稍后重试' });
      await nextTick();
      getCamerasInfo();
    }
  };

  // 获取客户端IP（占位实现）
  const getClientIP = async (): Promise<string> => {
    return '';
  };

  // 确认已经支付（刷新扫码支付状态）
  const confirmScanPaymentPaid = async () => {
    if (isRefreshingPayment.value) return;

    try {
      if (!currentOrderId.value) {
        showDialog({ message: '订单信息不完整，无法确认支付' });
        return;
      }

      isRefreshingPayment.value = true;
      const response = await refreshScanPayment({ orderId: currentOrderId.value });

      if (response.code === 200) {
        if (response.result?.success) {
          scanPaymentFailModalVisible.value = false;
          scanPaymentFailErrorMsg.value = '';
          showDialog({
            message: `支付成功！\n总金额：¥${currentPayAmount.value.toFixed(2)}\n共${
              currentFoodCount.value
            }份菜品`,
          });
          await refreshOrderListPreservingScroll();
        } else {
          const errorMsg = response.result?.errorMsg || '刷新支付状态失败，请重试';
          scanPaymentFailErrorMsg.value = errorMsg;
          showDialog({ message: errorMsg });
        }
      } else {
        const errorMsg = response.msg || '刷新支付状态失败，请稍后重试';
        scanPaymentFailErrorMsg.value = errorMsg;
        showDialog({ message: errorMsg });
      }
    } catch (error) {
      console.error('刷新支付状态失败:', error);
      const errorMsg = '刷新支付状态失败，请稍后重试';
      scanPaymentFailErrorMsg.value = errorMsg;
      showDialog({ message: errorMsg });
    } finally {
      isRefreshingPayment.value = false;
    }
  };

  // 取消扫码支付失败确认
  const cancelScanPaymentConfirm = () => {
    scanPaymentFailModalVisible.value = false;
    scanPaymentFailErrorMsg.value = '';
    isRefreshingPayment.value = false;
  };

  // 新增：判断是否可以开具发票
  const canGenerateInvoice = (order: OrderListItemModel) => {
    // 如果发票状态为已开票（invoiceStatus = 1），则不允许再次开票
    const invoiceStatus = (order as any).invoiceStatus;
    if (Number(invoiceStatus) === 1) {
      return false;
    }

    // 订单必须是已完成状态
    if (order.orderStatus !== 10) {
      return false;
    }

    // 检查支付状态
    if (order.paymentStatusName) {
      const statusLower = order.paymentStatusName.toLowerCase();
      // 支付状态必须包含"已支付"、"支付成功"等关键词
      return (
        statusLower.includes('已支付') ||
        statusLower.includes('支付成功') ||
        statusLower.includes('paid') ||
        statusLower.includes('success')
      );
    }

    // 如果没有支付状态信息，但订单已完成且没有待支付金额，也可以开票
    if (order.orderStatus === 10 && (!order.remainAmount || order.remainAmount <= 0)) {
      return true;
    }

    return false;
  };

  // 新增：判断是否可以取消订单
  const canCancelOrder = (order: OrderListItemModel) => {
    if (Number(order.orderType) !== 0) {
      return false;
    }

    if (Number(order.isRefund) === 1) {
      return false;
    }

    // 只有未确认的订单（状态为1）才可以取消
    return order.orderStatus === 1 || order.orderStatus === 2;
  };

  // 新增：判断是否可以打印收银小票
  const canPrintReceipt = (order: OrderListItemModel) => {
    // 已取消订单不允许打印收银小票
    if (order.orderStatus === 999) return false;

    // 已支付或已完成状态都可以打印收银小票
    // 检查支付状态
    if (order.paymentStatusName) {
      const statusLower = order.paymentStatusName.toLowerCase();
      // 已支付状态
      if (
        statusLower.includes('已支付') ||
        statusLower.includes('支付成功') ||
        statusLower.includes('paid') ||
        statusLower.includes('success')
      ) {
        return true;
      }
    }
    // 订单已完成状态（状态为10）
    if (order.orderStatus === 10) {
      return true;
    }
    // 如果没有支付状态信息，但订单已完成且没有待支付金额，也可以打印
    if (order.orderStatus === 10 && (!order.remainAmount || order.remainAmount <= 0)) {
      return true;
    }
    return false;
  };

  // 新增：判断是否可以打印出餐标签
  const canPrintPickLabel = (order: OrderListItemModel) => {
    // 已取消订单不允许打印出餐标签
    if (order.orderStatus === 999) {
      return false;
    }

    // 订单已完成时不显示，因为已完成就不需要补打出餐标签
    if (order.orderStatus === 10) {
      return false;
    }

    // 只有已支付并且有展示用取餐码才能打印出餐标签
    const pickCode = getDisplayPickCode(order);
    if (!pickCode || pickCode.trim() === '') {
      return false;
    }

    // 检查支付状态
    if (order.paymentStatusName) {
      const statusLower = order.paymentStatusName.toLowerCase();
      // 必须是已支付状态
      return (
        statusLower.includes('已支付') ||
        statusLower.includes('支付成功') ||
        statusLower.includes('paid') ||
        statusLower.includes('success')
      );
    }

    return false;
  };

  // 新增：打印收银小票
  const printReceipt = async (order: OrderListItemModel) => {
    if (!order.orderId) {
      showToast('订单信息不完整，无法打印');
      return;
    }

    try {
      const response = await orderPrint({ orderId: order.orderId });
      if (response.code === 200) {
        showToast('收银小票打印成功');
      } else {
        showToast(response.msg || '打印失败');
      }
    } catch (error) {
      console.error('打印收银小票失败:', error);
      showToast('打印收银小票失败，请稍后重试');
    }
  };

  // 打印订单小票
  const printOrderSlip = async (order: OrderListItemModel) => {
    if (!order.orderId) {
      showToast('订单信息不完整，无法打印');
      return;
    }

    try {
      const response = await orderSlipPrint({ orderId: order.orderId });
      if (response.code === 200) {
        showToast('订单小票打印成功');
      } else {
        showToast(response.msg || '打印失败');
      }
    } catch (error) {
      console.error('打印订单小票失败:', error);
      showToast('打印订单小票失败，请稍后重试');
    }
  };

  // 打印预定订单小票
  const printBookOrderSlip = async (order: OrderListItemModel) => {
    if (!order.orderId) {
      showToast('订单信息不完整，无法打印');
      return;
    }

    try {
      const response = await bookOrderSlipPrint({ orderId: order.orderId });
      if (response.code === 200) {
        showToast('预定订单小票打印成功');
        await refreshOrderListPreservingScroll();
      } else {
        showToast(response.msg || '打印失败');
      }
    } catch (error) {
      console.error('打印预定订单小票失败:', error);
      showToast('打印预定订单小票失败，请稍后重试');
    }
  };

  // 打印外卖订单小票
  const printDeliveryOrderSlip = async (order: OrderListItemModel) => {
    if (!order.orderId) {
      showToast('订单信息不完整，无法打印');
      return;
    }

    try {
      const response = await deliveryOrderSlipPrint({ orderId: order.orderId });
      if (response.code === 200) {
        showToast('外卖订单小票打印成功');
        await refreshOrderListPreservingScroll();
      } else {
        showToast(response.msg || '打印失败');
      }
    } catch (error) {
      console.error('打印外卖订单小票失败:', error);
      showToast('打印外卖订单小票失败，请稍后重试');
    }
  };

  // 新增：打印出餐标签
  const printPickLabel = async (order: OrderListItemModel) => {
    if (!order.orderId) {
      showToast('订单信息不完整，无法打印');
      return;
    }

    const pickCode = getDisplayPickCode(order);
    if (!pickCode || pickCode.trim() === '') {
      showToast('订单没有取餐码，无法打印出餐标签');
      return;
    }

    try {
      const response = await orderFoodPrint({ orderId: order.orderId });
      if (response.code === 200) {
        showToast('出餐标签打印成功');
      } else {
        showToast(response.msg || '打印失败');
      }
    } catch (error) {
      console.error('打印出餐标签失败:', error);
      showToast('打印出餐标签失败，请稍后重试');
    }
  };

  // 打开取消订单弹层
  const cancelOrderAction = (order: OrderListItemModel) => {
    if (!order.orderId) {
      showToast('订单信息不完整，无法取消');
      return;
    }

    currentCancelOrder.value = order;
    cancelRemark.value = '';
    cancelOrderModalVisible.value = true;
  };

  const closeCancelOrderModal = () => {
    if (isCancellingOrder.value) {
      return;
    }

    cancelOrderModalVisible.value = false;
    currentCancelOrder.value = null;
    cancelRemark.value = '';
  };

  const confirmCancelOrder = async () => {
    const order = currentCancelOrder.value;
    if (!order?.orderId || isCancellingOrder.value) {
      return;
    }

    isCancellingOrder.value = true;
    try {
      const response = await cancelOrder({
        orderId: order.orderId,
        cancelRemark: cancelRemark.value.trim(),
      });
      if (response.code === 200) {
        showToast(`订单 #${order.orderNo} 已取消！`);
        cancelOrderModalVisible.value = false;
        currentCancelOrder.value = null;
        cancelRemark.value = '';
        // 重新加载订单列表以获取最新状态
        await refreshOrderListPreservingScroll();
      } else {
        showToast(response.msg || '取消订单失败');
      }
    } catch (error) {
      console.error('取消订单失败:', error);
      showToast('取消订单失败，请稍后重试');
    } finally {
      isCancellingOrder.value = false;
    }
  };

  // 新增：判断是否可以申请退款
  const canApplyRefund = (order: OrderListItemModel) => {
    if (Number(order.orderType) !== 0) {
      return false;
    }

    // 检查支付状态：paymentStatus > 0（已完成/部分完成）时显示
    const paymentStatus = order.paymentStatus;

    // 如果存在 paymentStatus 字段，直接判断
    if (paymentStatus !== undefined && paymentStatus !== null) {
      if (Number(paymentStatus) <= 0) {
        return false; // 支付状态为0（待支付）或小于0，不能申请退款
      }
    } else {
      // 如果没有 paymentStatus 字段，通过 paymentStatusName 判断
      if (order.paymentStatusName) {
        const statusLower = order.paymentStatusName.toLowerCase();
        // 必须是已支付或部分支付状态
        const isPaidOrPartial =
          statusLower.includes('已支付') ||
          statusLower.includes('支付成功') ||
          statusLower.includes('部分支付') ||
          statusLower.includes('paid') ||
          statusLower.includes('success') ||
          statusLower.includes('partial');
        if (!isPaidOrPartial) {
          return false;
        }
      } else {
        // 如果没有支付状态信息，检查是否有已支付金额
        if (!order.paymentAmount || order.paymentAmount <= 0) {
          return false;
        }
      }
    }

    const remainingRefundable = (order.foodList || []).reduce(
      (total, food) =>
        total +
        Math.max(
          0,
          Number(food.amount || 0) -
            Number(food.orderDiscountAmount || 0) -
            Number(food.refundAmount || 0),
        ),
      0,
    );
    if (remainingRefundable <= 0) {
      return false;
    }

    return true;
  };

  // 新增：判断是否可以完成退款
  const canCompleteRefund = (order: OrderListItemModel) => {
    // 检查退款状态：refundStatus 为 0（待退款）时可以完成退款
    const refundStatus = order.refundStatus;
    // 确保 refundStatus 存在且等于 0（待退款）
    if (refundStatus === undefined || refundStatus === null) {
      return false;
    }
    // 支持数字和字符串类型的比较，refundStatus = 0 时显示完成退款按钮
    return Number(refundStatus) === 0;
  };

  // 新增：打开申请退款对话框
  const buildRefundFoodSelections = (
    foodList: FoodList[],
    refundList: RefundDetailItem[],
  ): RefundFoodSelection[] => {
    const refundedQuantities = new Map<number, number>();
    refundList
      .filter((refund) => Number(refund.refundStatus) !== 2)
      .forEach((refund) => {
        (refund.foodList || []).forEach((food) => {
          if (!food.orderFoodId) return;
          refundedQuantities.set(
            food.orderFoodId,
            (refundedQuantities.get(food.orderFoodId) || 0) + Number(food.quantity || 0),
          );
        });
      });

    return foodList.map((food, index) => {
      const orderFoodId = Number(food.orderFoodId || 0);
      const originalQuantity = Number(food.quantity || 0);
      const refundedQuantity = Math.min(originalQuantity, refundedQuantities.get(orderFoodId) || 0);
      const remainingQuantity = Math.max(0, originalQuantity - refundedQuantity);
      const refundableAmount = roundMoney(
        Math.max(
          0,
          Number(food.amount || 0) -
            Number(food.orderDiscountAmount || 0) -
            Number(food.refundAmount || 0),
        ),
      );
      const disabled =
        !orderFoodId ||
        Number(food.status) === 999 ||
        remainingQuantity <= 0 ||
        refundableAmount <= 0;
      return {
        orderFoodId,
        line: Number(food.line ?? index + 1),
        foodName: food.foodName || food.shopFoodName || '-',
        foodSpecName: food.foodSpecName || '标准',
        originalQuantity,
        refundedQuantity,
        remainingQuantity,
        selectedQuantity: disabled ? 0 : 1,
        price: Number(food.price || 0),
        refundableAmount,
        selected: false,
        disabled,
      };
    });
  };

  const openRefundModal = async (order: OrderListItemModel) => {
    if (!order.orderId) {
      showToast('订单信息不完整，无法申请退款');
      return;
    }
    currentRefundOrder.value = order;
    refundForm.value = {
      refundType: 1,
      refundReason: '',
    };
    refundFoodSelections.value = [];
    showRefundModal.value = true;
    try {
      isLoadingRefundDetail.value = true;
      const response = await getOrder({ orderId: order.orderId });
      if (response.code !== 200 || !response.result) {
        showToast(response.msg || '加载订单菜品失败');
        return;
      }
      const detail = response.result as OrderDetailModel & OrderDetailExtra;
      currentRefundOrder.value = { ...order, ...detail };
      refundFoodSelections.value = buildRefundFoodSelections(
        detail.foodList || [],
        detail.refundList || [],
      );
    } catch (error) {
      console.error('加载退款菜品失败:', error);
      showToast('加载订单菜品失败，请稍后重试');
    } finally {
      isLoadingRefundDetail.value = false;
    }
  };

  // 新增：关闭申请退款对话框
  const closeRefundModal = () => {
    showRefundModal.value = false;
    currentRefundOrder.value = null;
    refundFoodSelections.value = [];
    refundForm.value = {
      refundType: 1,
      refundReason: '',
    };
  };

  // 新增：提交退款申请
  const submitRefund = async () => {
    if (!currentRefundOrder.value || !currentRefundOrder.value.orderId) {
      showToast('订单信息不完整，无法申请退款');
      return;
    }
    if (![1, 2].includes(refundForm.value.refundType)) {
      showToast('请选择退款类型');
      return;
    }

    const selectedFoods = refundFoodSelections.value.filter((item) => item.selected);
    if (selectedFoods.length === 0) {
      showToast('请至少选择一个退款菜品');
      return;
    }
    const hasInvalidQuantity = selectedFoods.some(
      (item) =>
        !Number.isInteger(item.selectedQuantity) ||
        item.selectedQuantity <= 0 ||
        item.selectedQuantity > item.remainingQuantity,
    );
    if (hasInvalidQuantity) {
      showToast('退款数量必须为不超过可退数量的正整数');
      return;
    }
    const amount = selectedRefundAmount.value;
    if (amount <= 0) {
      showToast('所选菜品没有可退款金额');
      return;
    }

    // 验证退款原因
    if (!refundForm.value.refundReason || refundForm.value.refundReason.trim() === '') {
      showToast('请输入退款原因');
      return;
    }

    try {
      isSubmittingRefund.value = true;
      const response = await insertOrderRefund({
        orderId: currentRefundOrder.value.orderId,
        refundType: refundForm.value.refundType,
        refundStatus: 0,
        amount,
        refundTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        refundReason: refundForm.value.refundReason.trim(),
        remark: '',
        isValidateStatus: 1,
        foodList: selectedFoods.map((item) => ({
          orderFoodId: item.orderFoodId,
          line: item.line,
          quantity: item.selectedQuantity,
          price: item.price,
          amount: getSelectedRefundAmount(item),
          remark: '',
        })),
      });

      if (response.code === 200) {
        showDialog({
          message: `退款申请已提交成功！\n订单号：${
            currentRefundOrder.value.orderNo
          }\n退款金额：¥${amount.toFixed(2)}`,
        });
        closeRefundModal();
        // 部分退款在创建时已完成，使用服务端状态决定是否显示“完成退款”。
        await refreshOrderListPreservingScroll();
      } else {
        showDialog({ message: response.msg || '申请退款失败' });
      }
    } catch (error) {
      console.error('申请退款失败:', error);
      showDialog({ message: '申请退款失败，请稍后重试' });
    } finally {
      isSubmittingRefund.value = false;
    }
  };

  // 新增：完成退款操作
  const completeRefundAction = async (order: OrderListItemModel) => {
    if (!order.orderId) {
      showToast('订单信息不完整，无法完成退款');
      return;
    }

    const orderId = order.orderId;
    if (completingRefundOrderIds.value.includes(orderId)) return;

    if (!confirm(`确定要完成订单 #${order.orderNo} 的退款吗？`)) {
      return;
    }

    try {
      completingRefundOrderIds.value.push(orderId);
      const response = await orderRefundComplete({
        orderId,
      });

      if (response.code === 200) {
        showDialog({
          message: `退款已完成！\n订单号：${order.orderNo}`,
        });
        await refreshOrderListPreservingScroll();
      } else {
        showDialog({ message: response.msg || '完成退款失败' });
      }
    } catch (error) {
      console.error('完成退款失败:', error);
      showDialog({ message: '完成退款失败，请稍后重试' });
    } finally {
      completingRefundOrderIds.value = completingRefundOrderIds.value.filter(
        (id) => id !== orderId,
      );
    }
  };

  // 生命周期
  onMounted(async () => {
    // 设置默认日期为今天
    const today = new Date().toISOString().split('T')[0];
    filters.value.startDate = today;
    filters.value.endDate = today;

    // 加载基础数据
    await loadShopList();
    await loadOrderList(); // 加载订单列表
  });
</script>

<style scoped>
  .refund-modal-overlay {
    box-sizing: border-box;
    padding: max(1rem, env(safe-area-inset-top)) max(1rem, env(safe-area-inset-right))
      max(1rem, env(safe-area-inset-bottom)) max(1rem, env(safe-area-inset-left));
  }

  .refund-modal-panel {
    height: calc(100vh - 2rem);
    max-width: 64rem;
  }

  @supports (height: 100dvh) {
    .refund-modal-panel {
      height: calc(100dvh - 2rem);
    }
  }

  .refund-modal-body {
    min-height: 0;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .refund-food-scroll {
    max-height: 38vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .refund-food-scroll thead {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  @media (max-height: 820px) {
    .refund-modal-overlay {
      padding-top: max(0.5rem, env(safe-area-inset-top));
      padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
    }

    .refund-modal-panel {
      height: calc(100vh - 1rem);
    }

    .refund-modal-body {
      padding: 1rem;
    }

    .refund-food-scroll {
      max-height: 30vh;
    }

    .refund-reason {
      height: 5rem;
    }
  }

  @media (max-width: 767px) {
    .refund-modal-overlay {
      padding-right: max(0.5rem, env(safe-area-inset-right));
      padding-left: max(0.5rem, env(safe-area-inset-left));
    }

    .refund-modal-panel {
      border-radius: 0.5rem;
    }
  }

  /* 自定义样式 */
  .transition-colors {
    transition: background-color 0.2s ease;
  }
</style>
